<template>
  <f7-page name="informe-abonos-cobros" @page:init="onPageInit">
    <f7-navbar>
      <f7-nav-left><f7-link back icon-f7="arrow_left"></f7-link></f7-nav-left>
      <f7-nav-title>Abonos abiertos</f7-nav-title>
      <f7-nav-right>
        <!-- Los dos botones de la funcionalidad de reportes que ya tiene la ERP. No se dibujan si
             el servidor ha dicho que esta persona no puede con este informe (403): un botón que
             lleva a un rechazo hace creer que le han quitado permisos. -->
        <template v-if="!sinPermiso">
          <button class="navbar-btn" @click="abrirEnvio">Enviar reporte</button>
          <button class="navbar-btn" @click="abrirProgramaciones">Programar reporte</button>
        </template>
        <f7-link @click="cargar" icon-f7="arrow_2_circlepath" style="color:#c8d3e5"></f7-link>
      </f7-nav-right>
    </f7-navbar>
    <!-- #16 · se lee en directo de la consulta de Facturas en la propia petición, sin caché. Y
         solo cuando hay cifras en pantalla, igual que el Centro de Cobros. -->
    <div class="sello-datos-bar" v-if="hayCifras"><sello-datos estado="vivo" /></div>
    <f7-page-content style="padding-top:0">

      <!-- LO QUE QUEDÓ A MEDIAS DE ANTES DE RECARGAR.
           Se enseña en la pantalla principal y no solo dentro de los dos popups porque quien ha
           recargado —o ha cerrado la pestaña y vuelto— no sabe que hay algo a medias y no tiene por
           qué abrir nada para enterarse. Antes esto se perdía con el estado del componente y lo
           siguiente que se pulsara iba a ciegas (@Donchurru, 15/10/2026, opción C). -->
      <div class="bloque" v-if="dudasAbiertas.length">
        <p class="bloque-titulo">Operaciones sin resolver</p>
        <p class="bloque-explica">
          Se quedaron a medias y no se sabe si llegaron a aplicarse. Resuélvelas antes de volver a
          enviar o a programar: comprobar es seguro y reintentar no duplica nada.
        </p>
        <div v-for="abierta in dudasAbiertas" :key="abierta.clave" class="regla">
          <p class="regla-detalle"><strong>{{ abierta.que }}</strong></p>
          <!-- Si lo que se ve en su parte de la pantalla es lo de esta operación o lo de otra.
               Desde aquí se repite una que puede no ser la que está delante, y sin decirlo se lee
               como que se manda lo que se ve. -->
          <p class="regla-detalle" v-if="abierta.queSeVe">{{ abierta.queSeVe }}</p>
          <aviso-operacion-dudosa :duda="abierta.duda"
            :motivo-espera="porQueNoSeRepite(abierta.duda)"
            @reintentar="reintentar(abierta.duda)"
            @comprobar="comprobar(abierta.duda)"
            @dar-por-visto="darPorVisto(abierta.duda)" />
          <div class="regla-botones">
            <button class="btn-pequeno" @click="abrirDondeVive(abierta)">
              Ver dónde está
            </button>
          </div>
        </div>
      </div>

      <!-- LO QUE EL SERVIDOR HA DICHO QUE NO SE APLICÓ.
           Esto ya no es una duda: se sabe, y se sabe que no se hizo. Pero lo que se escribió sigue
           siendo lo único con lo que repetirlo, así que no se tira solo. Se enseña porque hasta
           ahora no se veía en ninguna parte: quien resolvía dos operaciones del mismo sitio se
           encontraba el formulario relleno sin nada que dijera de qué, y el aviso que lo contaba era
           un mensaje que ya se había ido de la pantalla (@Donchurru, 16/10/2026, planteamiento B). -->
      <div class="bloque" v-if="sinAplicar.length">
        <p class="bloque-titulo">Sin aplicar</p>
        <p class="bloque-explica">
          Estas operaciones <strong>no llegaron a hacerse</strong>, así que nada ha cambiado. Lo que
          habías escrito se conserva: repítelas si siguen haciendo falta, o descártalas.
        </p>
        <div v-for="suelta in sinAplicar" :key="suelta.clave" class="regla">
          <p class="regla-detalle"><strong>{{ suelta.que }}</strong></p>
          <div class="aviso-sin-aplicar">
            <p class="regla-detalle">{{ suelta.porQue }}</p>
            <!-- Cuál de ellas es la que tiene los campos rellenos ahora mismo: es la pregunta que se
                 hace quien los ve, y con dos de un mismo sitio no se puede adivinar. -->
            <p class="regla-detalle" v-if="suelta.queSeVe">{{ suelta.queSeVe }}</p>
            <div class="regla-botones">
              <!-- «Volver a intentarlo» NO se dibuja con la clave gastada: ahí el servidor no
                   acepta la misma operación otra vez, así que el botón no llevaría a nada. -->
              <button class="btn-pequeno btn-pequeno-azul" v-if="suelta.sePuedeRepetir"
                :disabled="!!porQueNoSeRepite(suelta.apunte)"
                @click="reintentar(suelta.apunte)">Volver a intentarlo</button>
              <button class="btn-pequeno" @click="descartarSinAplicar(suelta.apunte)">
                Descartar lo escrito
              </button>
              <button class="btn-pequeno" @click="abrirDondeVive(suelta)">Ver dónde está</button>
            </div>
            <!-- §9 · por qué no se puede pulsar y qué lo desbloquea, junto al control. -->
            <p class="regla-detalle" v-if="suelta.sePuedeRepetir && porQueNoSeRepite(suelta.apunte)">
              {{ porQueNoSeRepite(suelta.apunte) }}
            </p>
          </div>
        </div>
      </div>


      <f7-block v-if="cargando" class="text-align-center"><f7-preloader></f7-preloader></f7-block>

      <f7-block v-if="error" class="aviso-error">{{ error }}</f7-block>

      <template v-if="dto && !cargando">

        <!-- D-COB-5 · qué parte de la cartera se está contando. Lo dice el servidor, que es quien
             aplica el criterio: si lo escribiera la pantalla, podría decir «toda la empresa» sobre
             una lectura recortada. -->
        <div class="linea-alcance">
          <f7-icon f7="person_crop_circle" class="linea-alcance-icono"></f7-icon>
          <span>{{ dto.alcance }}</span>
        </div>

        <!-- Que el informe no escribe nada, con palabras y arriba: es lo primero que se pregunta de
             una pantalla que propone casar abonos (D-COB-9). El texto lo manda la API. -->
        <div class="nota-dato nota-dato-ok" v-if="dto.notaSoloLectura">
          <f7-icon f7="info_circle" class="nota-dato-icono-ok"></f7-icon>
          <span>{{ dto.notaSoloLectura }}</span>
        </div>

        <!-- TRES CIFRAS DE CABECERA -->
        <div class="titulares">
          <div class="titular">
            <p class="titular-rotulo">Abonos abiertos</p>
            <p class="titular-cifra">{{ formatEuros(totales.importe) }}</p>
            <p class="titular-nota">{{ totales.abonos }} {{ palabraAbonos(totales.abonos) }}</p>
          </div>
          <div class="titular">
            <p class="titular-rotulo">Más de un año</p>
            <p class="titular-cifra titular-cifra-alerta">
              {{ formatEuros(masDeUnAnio.importe) }}
            </p>
            <p class="titular-nota">
              {{ masDeUnAnio.abonos }} {{ palabraAbonos(masDeUnAnio.abonos) }} ·
              más de 365 días sin casar
            </p>
          </div>
          <div class="titular">
            <p class="titular-rotulo">Un año o menos</p>
            <p class="titular-cifra">{{ formatEuros(resto.importe) }}</p>
            <p class="titular-nota">{{ resto.abonos }} {{ palabraAbonos(resto.abonos) }}</p>
            <!-- Estos están contados aquí sin que se sepa de cuándo son, así que se dice en la
                 propia tarjeta y no al pie: quien lee «un año o menos» tiene que verlo aquí
                 (D-COB-8). -->
            <p class="titular-nota titular-nota-aviso" v-if="haySinFecha">
              {{ sinFecha.abonos }} sin fecha de emisión ({{ formatEuros(sinFecha.importe) }}): no
              se puede medir su antigüedad
            </p>
          </div>
        </div>

        <!-- EN QUÉ ESTADO ESTÁ LA CASACIÓN -->
        <div class="recuento-sugerencias">
          <span class="chip chip-ok">{{ dto.abonosConSugerenciaUnica }} con una sola candidata</span>
          <span class="chip chip-aviso">{{ dto.abonosConVariasCandidatas }} con varias posibles</span>
          <!-- #537 · los que no tienen candidata suelta pero sí un conjunto de facturas que suma su
               total: es el recuento que pidió la incidencia para saber cuántos dejan de ser «no
               deducibles». De esos se dice aparte cuántos tienen más de un conjunto, que son los
               que Administración tiene que mirar para elegir. -->
          <span class="chip chip-ok">
            {{ dto.abonosConConjuntoSugerido }} con un conjunto de facturas
            <template v-if="dto.abonosConVariosConjuntos > 0">
              ({{ dto.abonosConVariosConjuntos }} con varios posibles)
            </template>
          </span>
          <span class="chip chip-neutro">{{ dto.abonosNoDeducibles }} no deducibles</span>
          <!-- Los que no se han podido calcular, que no es lo mismo que los que no tienen conjunto.
               Solo se enseña cuando hay alguno: un recuento que casi siempre dice cero enseña a no
               leer los recuentos. -->
          <span class="chip chip-alerta" v-if="dto.abonosConDemasiadasFacturas > 0">
            {{ dto.abonosConDemasiadasFacturas }} sin calcular · más de
            {{ dto.topeFacturasPorCliente }} facturas abiertas del cliente
          </span>
        </div>

        <div class="nota-dato nota-dato-alerta" v-if="dto.filasOmitidas > 0">
          <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
          <span>
            Las listas enseñan {{ dto.filasOmitidas }} filas menos que el total: es el tope de
            filas del informe. Las cifras de arriba y el reparto por año están calculados sobre
            todos los abonos.
          </span>
        </div>

        <!-- REPARTO POR AÑO DE EMISIÓN -->
        <div class="bloque">
          <p class="bloque-titulo">Reparto por año de emisión</p>
          <table class="tabla tabla-reparto">
            <thead>
              <tr><th>Año</th><th class="num">Abonos</th><th class="num">Importe</th></tr>
            </thead>
            <tbody>
              <tr v-for="fila in reparto" :key="fila.etiqueta">
                <td>{{ fila.etiqueta }}</td>
                <td class="num">{{ fila.abonos }}</td>
                <td class="num">{{ formatEuros(fila.importe) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td class="num">{{ totales.abonos }}</td>
                <td class="num">{{ formatEuros(totales.importe) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- LAS DOS LISTAS, SEPARADAS POR EL SERVIDOR -->
        <div class="bloque">
          <p class="bloque-titulo">Más de un año sin casar</p>
          <p class="bloque-explica">
            Van aparte porque son los que hay que cerrar primero: llevan más de 365 días abiertos.
          </p>
          <tabla-abonos :filas="dto.masDeUnAnio" :totales="masDeUnAnio" />
        </div>

        <div class="bloque">
          <p class="bloque-titulo">Resto de abonos abiertos</p>
          <tabla-abonos :filas="dto.restoDeAbonos" :totales="resto" />
        </div>

        <!-- LAS TRES NOTAS DEL INFORME. Las manda la API: son decisiones sobre el dato
             (D-COB-2, el corte de un año y qué es una candidata), no frases de pantalla. -->
        <div class="nota-dato nota-dato-pie" v-if="dto.notaImporte">
          <f7-icon f7="info_circle" class="nota-dato-icono"></f7-icon>
          <span>{{ dto.notaImporte }}</span>
        </div>
        <div class="nota-dato nota-dato-pie" v-if="dto.notaAntiguedad">
          <f7-icon f7="info_circle" class="nota-dato-icono"></f7-icon>
          <span>{{ dto.notaAntiguedad }}</span>
        </div>
        <div class="nota-dato nota-dato-pie" v-if="dto.notaSugerencia">
          <f7-icon f7="info_circle" class="nota-dato-icono"></f7-icon>
          <span>{{ dto.notaSugerencia }}</span>
        </div>
        <div class="nota-dato nota-dato-pie" v-if="dto.notaConjunto">
          <f7-icon f7="info_circle" class="nota-dato-icono"></f7-icon>
          <span>{{ dto.notaConjunto }}</span>
        </div>

      </template>
    </f7-page-content>

    <!-- ENVIAR REPORTE -->
    <f7-popup :opened="envio.abierto" @popup:closed="envio.abierto = false"
      style="--f7-popup-tablet-width:600px">
      <f7-page>
        <f7-navbar title="Enviar el informe por correo">
          <f7-nav-right><f7-link popup-close icon-f7="xmark"></f7-link></f7-nav-right>
        </f7-navbar>
        <f7-page-content>
          <f7-block-title>Destinatarios</f7-block-title>
          <!-- Bloqueado mientras se manda, por lo mismo que el formulario de la programación: lo
               que se escriba después no sale en el correo que ya va de camino, y al reintentar hay
               que mandar exactamente lo mismo para que el servidor lo reconozca y no mande dos. -->
          <f7-block strong>
            <input v-model="envio.emails" type="text" class="campo" :disabled="envioBloqueado"
              placeholder="correo@empresa.com, otro@empresa.com">
            <p class="campo-ayuda">Separados por comas.</p>
          </f7-block>
          <f7-block-title>Asunto</f7-block-title>
          <f7-block strong>
            <input v-model="envio.asunto" type="text" class="campo" :disabled="envioBloqueado"
              placeholder="Abonos abiertos — SIMECAL">
            <p class="campo-ayuda">Si lo dejas en blanco va el asunto de siempre con la fecha de
              hoy.</p>
          </f7-block>
          <!-- Lo que el correo lleva lo decide el servidor al mandarlo, no esta pantalla: el POST
               manda destinatarios y asunto, y el informe se vuelve a generar allí. Así que no se
               puede prometer que lleve «estas» cifras —si un abono cambia entre que se abre la
               pantalla y se pulsa Enviar, llevará las de ese momento—. Decirlo es lo que hace que
               las dos cosas no se contradigan cuando no coinciden. -->
          <f7-block>
            <p class="campo-ayuda">
              Se manda el informe de abonos abiertos <strong>recién calculado en el momento del
              envío</strong>, así que si algún abono cambia desde que abriste esta pantalla, el
              correo llevará las cifras de entonces y no las de aquí.
              Mandarlo no cambia ningún abono ni ninguna factura.
            </p>
            <p class="campo-ayuda" v-if="hayCifras">
              Lo que hay ahora en pantalla: {{ totales.abonos }}
              {{ palabraAbonos(totales.abonos) }} · {{ formatEuros(totales.importe) }}. Si quieres
              comprobar que el correo coincide, recarga el informe con el botón de recargar antes
              de enviarlo.
            </p>
          </f7-block>
          <f7-block v-if="envio.error" class="aviso-error">{{ envio.error }}</f7-block>
          <f7-block>
            <f7-button fill color="green" :disabled="!hayDestinatarios || envioBloqueado"
              @click="enviarAhora">
              {{ enviandoAhora ? 'Enviando…' : 'Enviar' }}
            </f7-button>
            <!-- §9 · un control que no se puede usar dice por qué y quién lo desbloquea, y lo dice
                 JUNTO al control: quien pulsa no relaciona un aviso del principio de la pantalla
                 con el botón de abajo. -->
            <p class="campo-ayuda campo-ayuda-aviso" v-if="!hayDestinatarios">
              Escribe al menos un correo en «Destinatarios», arriba, para poder enviarlo.
            </p>
            <!-- No se sabe si el correo ha salido. No se dice que no, porque puede haber salido,
                 y tampoco se deja el botón suelto. Las tres salidas —reintentar la MISMA operación,
                 preguntar al servidor cómo quedó, o darla por vista— y cuándo se ofrece cada una
                 están en `aviso-operacion-dudosa.vue`, que es el MISMO bloque que usan el
                 formulario de la programación, cada regla y el aviso de arriba. Tenerlo repartido
                 es lo que hizo que cada ronda de revisión arreglara uno de los tres sitios y
                 dejara los otros dos. -->
            <aviso-operacion-dudosa v-if="dudaEnvio" :duda="dudaEnvio"
              :motivo-espera="porQueNoSeRepite(dudaEnvio)"
              @reintentar="reintentar(dudaEnvio)"
              @comprobar="comprobar(dudaEnvio)"
              @dar-por-visto="darPorVisto(dudaEnvio)" />
            <!-- Y cuando el aviso que cierra esto NO es el de la operación que tiene los campos
                 delante, su aviso está arriba y se dice: pintarlo aquí sería poner sus botones al
                 lado de los datos de otra. El de la que está delante, debajo. -->
            <p class="campo-ayuda campo-ayuda-aviso" v-if="cerradoFueraEnvio">
              {{ cerradoFueraEnvio }}
            </p>
            <p class="campo-ayuda campo-ayuda-aviso" v-if="delanteEnvio">{{ delanteEnvio }}</p>
          </f7-block>
        </f7-page-content>
      </f7-page>
    </f7-popup>

    <!-- PROGRAMAR REPORTE -->
    <f7-popup :opened="programacion.abierto" @popup:closed="programacion.abierto = false"
      style="--f7-popup-tablet-width:680px">
      <f7-page>
        <f7-navbar title="Programar el envío del informe">
          <f7-nav-right><f7-link popup-close icon-f7="xmark"></f7-link></f7-nav-right>
        </f7-navbar>
        <f7-page-content>
          <f7-block v-if="programacion.cargando" class="text-align-center">
            <f7-preloader></f7-preloader>
          </f7-block>
          <f7-block v-if="programacion.error" class="aviso-error">{{ programacion.error }}</f7-block>

          <template v-if="!programacion.cargando">
            <f7-block-title>Envíos programados</f7-block-title>
            <f7-block strong v-if="!programacion.lista.length">
              <p class="campo-ayuda">Todavía no hay ninguno. El formulario de abajo da de alta el
                primero.</p>
            </f7-block>
            <div v-for="regla in programacion.lista" :key="regla.id" class="regla"
              :class="{ 'regla-pausada': !regla.activa }">
              <div class="regla-cabecera">
                <span class="regla-punto" :class="regla.activa ? 'regla-punto-activa' : ''"></span>
                <strong>{{ regla.nombre || 'Sin nombre' }}</strong>
                <span class="chip chip-neutro">{{ textoFrecuencia(regla) }}</span>
              </div>
              <p class="regla-detalle">{{ textoDestinatarios(regla) }}</p>
              <p class="regla-detalle" v-if="regla.ultimoEnvio">
                Último envío: {{ formatFechaHora(regla.ultimoEnvio) }}
                ({{ regla.ultimoEstado || '—' }})
              </p>
              <!-- Los cuatro botones preguntan a `bloqueoDeRegla` y a nada más. Es a propósito un
                   solo sitio: cada vez que el cerrojo se ha repartido por botón, el siguiente
                   botón se ha quedado fuera —primero Pausar y Eliminar mientras se guardaba, y
                   después Pausar mientras se editaba, que es el que deshacía la pausa al guardar—.
                   Lo que decide quién puede escribir en una regla está en ese método y en ninguna
                   otra parte; el `:disabled` y el método del botón miran el mismo. -->
              <div class="regla-botones">
                <button class="btn-pequeno" :disabled="!!bloqueoDeRegla(regla, 'EDITAR')"
                  @click="editarRegla(regla)">Editar</button>
                <button class="btn-pequeno" :disabled="!!bloqueoDeRegla(regla)"
                  @click="alternarRegla(regla)">
                  {{ regla.activa ? 'Pausar' : 'Activar' }}
                </button>
                <button class="btn-pequeno btn-pequeno-azul" :disabled="!!bloqueoDeRegla(regla)"
                  @click="enviarRegla(regla)">
                  {{ enviando(regla) ? 'Enviando…' : 'Enviar ahora' }}
                </button>
                <button class="btn-pequeno btn-pequeno-rojo" :disabled="!!bloqueoDeRegla(regla)"
                  @click="borrarRegla(regla)">
                  Eliminar
                </button>
              </div>
              <!-- §9 · un control que no se puede usar dice por qué, quién lo desbloquea y dónde,
                   y lo dice junto al control. El motivo es el del propio cerrojo: no hay dos
                   textos que puedan contar cosas distintas. -->
              <p class="regla-detalle" v-if="bloqueoDeRegla(regla, 'EDITAR')">
                {{ bloqueoDeRegla(regla, 'EDITAR') }}
              </p>
              <!-- El estado DESCONOCIDO de esta regla: la petición se fue y no volvió respuesta, o
                   el servidor contestó que no la aplicó. No se afirma «no se ha hecho» —puede
                   haberse hecho—. El mismo bloque que el envío y el formulario, a propósito.
                   Decisión de @Donchurru del 15/10/2026. -->
              <aviso-operacion-dudosa v-if="pendienteDe(regla)" :duda="pendienteDe(regla)"
                :motivo-espera="porQueNoSeRepite(pendienteDe(regla))"
                @reintentar="reintentar(pendienteDe(regla))"
                @comprobar="comprobar(pendienteDe(regla))"
                @dar-por-visto="darPorVisto(pendienteDe(regla))" />
            </div>

            <f7-block-title>{{ programacion.editando ? 'Editar el envío' : 'Nuevo envío programado' }}</f7-block-title>
            <!-- TODO el formulario se bloquea mientras se guarda, no solo sus botones. Lo pidió
                 @Donchurru el 15/10/2026 y el motivo es concreto: el cuerpo del POST se copia al
                 pulsar «Guardar», así que lo que se escriba después no se manda, y al volver la
                 respuesta el formulario se limpia y se lo lleva sin decir nada. Con los campos
                 bloqueados no hay nada que perder, y de paso lo que se reintenta es exactamente lo
                 que se mandó —que es lo que el servidor reconoce como el mismo reintento—. -->
            <f7-block strong :class="{ 'form-bloqueado': formBloqueado }">
              <p class="campo-rotulo">Nombre</p>
              <input v-model="programacion.form.nombre" type="text" class="campo"
                :disabled="formBloqueado" placeholder="Abonos abiertos de los lunes">

              <p class="campo-rotulo">Cada cuánto</p>
              <div class="opciones">
                <button v-for="f in FRECUENCIAS" :key="f" class="opcion"
                  :class="{ 'opcion-elegida': programacion.form.frecuencia === f }"
                  :disabled="formBloqueado"
                  @click="programacion.form.frecuencia = f">{{ f }}</button>
              </div>

              <div class="fila-campos">
                <label class="campo-inline">
                  <span class="campo-rotulo">Hora</span>
                  <!-- `step` de cinco minutos: el envío automático solo pasa cada cinco, así que
                       las flechas del campo ofrecen justo las horas que se respetan. Escribir otra
                       a mano sigue siendo posible, y eso lo dice `avisoHora`. -->
                  <input v-model="programacion.form.hora" type="time" step="300"
                    class="campo campo-corto" :disabled="formBloqueado">
                </label>
                <label class="campo-inline" v-if="programacion.form.frecuencia === 'SEMANAL'">
                  <span class="campo-rotulo">Día de la semana</span>
                  <select v-model="programacion.form.diaSemana" class="campo campo-corto"
                    :disabled="formBloqueado">
                    <option v-for="d in DIAS_SEMANA" :key="d" :value="d">{{ d }}</option>
                  </select>
                </label>
                <label class="campo-inline" v-if="programacion.form.frecuencia === 'MENSUAL'">
                  <span class="campo-rotulo">Día del mes</span>
                  <input v-model.number="programacion.form.diaMes" type="number" min="1" max="28"
                    class="campo campo-corto" :disabled="formBloqueado">
                </label>
              </div>
              <!-- §9 · pegado a la fila de «Hora», que es el campo que lo arregla. -->
              <p class="campo-ayuda campo-ayuda-aviso" v-if="avisoHora">
                {{ avisoHora }}
              </p>
              <label class="campo-check">
                <input type="checkbox" v-model="programacion.form.soloLaborables"
                  :disabled="formBloqueado">
                Solo días laborables
              </label>
              <!-- §9 · el motivo va junto al control que lo desbloquea, no solo junto a «Guardar»:
                   quien elige el sábado está mirando aquí. -->
              <p class="campo-ayuda campo-ayuda-aviso" v-if="avisoFinDeSemana">
                {{ avisoFinDeSemana }}
              </p>

              <p class="campo-rotulo">Destinatarios</p>
              <input v-model="programacion.form.emailsExtra" type="text" class="campo"
                :disabled="formBloqueado" placeholder="correo@empresa.com, otro@empresa.com">
              <p class="campo-ayuda">Separados por comas.</p>

              <label class="campo-check">
                <input type="checkbox" v-model="programacion.form.activa" :disabled="formBloqueado">
                Activado
              </label>
              <p class="campo-ayuda">
                Pausado se guarda igual y no se manda nada hasta que se active.
              </p>
            </f7-block>
            <f7-block>
              <div class="botonera">
                <!-- Deshabilitado mientras se guarda: dos pulsaciones antes de que vuelva la
                     primera son dos altas, porque el formulario no se limpia hasta la respuesta y
                     las dos salen con `id: null`. Y dos programaciones iguales mandan el informe
                     dos veces en cada ejecución.
                     Ese cerrojo es del navegador y no basta por sí solo —lo salta un corte de red,
                     dos pestañas o dos personas—: lo que de verdad lo cierra es la clave de
                     operación con la que viaja cada escritura, que el servidor no acepta dos veces
                     (@Donchurru, 15/10/2026). -->
                <f7-button fill color="green" style="flex:1"
                  :disabled="!programacionGuardable || formBloqueado"
                  @click="guardarRegla">
                  {{ guardandoAhora ? 'Guardando…' : 'Guardar' }}
                </f7-button>
                <f7-button outline style="flex:1" :disabled="formBloqueado"
                  @click="limpiarForm">
                  {{ programacion.editando ? 'Cancelar la edición' : 'Limpiar' }}
                </f7-button>
              </div>
              <p class="campo-ayuda campo-ayuda-aviso" v-if="!programacionGuardable">
                {{ motivoNoGuardable }}
              </p>
              <!-- El estado DESCONOCIDO del formulario, y por qué no dice «no se ha guardado»: la
                   respuesta puede haberse perdido con el cambio ya aplicado, así que afirmarlo
                   sería mentir la mitad de las veces y llevaría a dar de alta lo mismo otra vez.
                   El formulario NO se desbloquea hasta que el servidor haya dicho qué pasó con esa
                   operación: soltarlo antes deja «Guardar» mandando el mismo alta con una clave
                   NUEVA, que el servidor ya no reconoce como reintento, y quedan dos programaciones
                   iguales mandando el informe dos veces.
                   §9 · el motivo, quién lo desbloquea y dónde, junto al control. -->
              <aviso-operacion-dudosa v-if="dudaForm" :duda="dudaForm"
                :motivo-espera="porQueNoSeRepite(dudaForm)"
                @reintentar="reintentar(dudaForm)"
                @comprobar="comprobar(dudaForm)"
                @dar-por-visto="darPorVisto(dudaForm)" />
              <!-- Lo mismo que en «Enviar reporte»: el aviso de aquí es el de la operación que
                   tiene estos campos delante, y si lo que cierra el formulario es otra, se dice
                   por qué y dónde está su aviso en vez de traer sus botones aquí (§9). -->
              <p class="campo-ayuda campo-ayuda-aviso" v-if="cerradoFueraForm">
                {{ cerradoFueraForm }}
              </p>
              <p class="campo-ayuda campo-ayuda-aviso" v-if="delanteForm">{{ delanteForm }}</p>
            </f7-block>

            <!-- El historial. Está aquí y no en otra pantalla porque es lo que contesta la única
                 pregunta que deja una programación: ¿salió? Cuando un envío falla, este apunte es
                 lo único que lo cuenta. -->
            <f7-block-title>Últimos envíos</f7-block-title>
            <f7-block v-if="programacion.envios.error" class="aviso-error">
              {{ programacion.envios.error }}
            </f7-block>
            <f7-block strong v-else-if="!programacion.envios.lista.length">
              <p class="campo-ayuda">Todavía no ha salido ninguno con una programación.</p>
            </f7-block>
            <table class="tabla" v-else>
              <thead>
                <tr><th>Cuándo</th><th>Cómo acabó</th><th>Buzones</th></tr>
              </thead>
              <tbody>
                <tr v-for="envio in programacion.envios.lista" :key="envio.id">
                  <td>
                    {{ formatFechaHora(envio.fechaEnvio) }}
                    <!-- De qué programación salió: con varias reglas, la fecha sola no lo dice. -->
                    <p class="campo-ayuda">{{ envio.asunto || 'Sin nombre' }}</p>
                  </td>
                  <td>
                    {{ textoEstadoEnvio(envio) }}
                    <!-- D-COB-8 · el motivo del fallo se enseña: un «ERROR» sin motivo obliga a
                         preguntar por dentro, y es justo lo que este historial venía a evitar. -->
                    <p class="campo-ayuda campo-ayuda-aviso" v-if="envio.errorDetalle">
                      {{ envio.errorDetalle }}
                    </p>
                  </td>
                  <td>{{ textoBuzonesEnvio(envio) }}</td>
                </tr>
              </tbody>
            </table>
            <f7-block>
              <!-- D-COB-8 · se dice qué NO está en esta lista. Si no, un envío hecho con «Enviar
                   reporte» que no aparece aquí se leería como un envío que no salió. -->
              <p class="campo-ayuda">
                Aquí salen los envíos de las programaciones, los de su hora y los de «Enviar ahora».
                Lo que se manda con «Enviar reporte» no se registra: ese botón manda el informe en el
                momento y a quien se escriba, y no es un envío programado. Así que esta lista
                <strong>no sirve para saber si un «Enviar reporte» salió</strong>: eso lo contesta
                el botón «Comprobar cómo quedó» del aviso de la propia operación.
              </p>
            </f7-block>
          </template>
        </f7-page-content>
      </f7-page>
    </f7-popup>
  </f7-page>
</template>

<style scoped>
.sello-datos-bar { padding: 6px 16px 0; }

.navbar-btn {
  font-size: 10px;
  padding: 4px 10px;
  margin-left: 6px;
  background: rgba(255,255,255,0.15);
  color: #c8d3e5;
  border: 0.5px solid rgba(255,255,255,0.25);
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  /* Framework7 le da `width:100%` a todo <button> por selector de tipo
     (framework7.bundle.css): sin estas tres líneas el botón se estira al ancho de la barra y las
     palabras se van al centro. No son decoración. */
  width: auto;
  display: inline-block;
  text-align: center;
}
.navbar-btn:focus-visible { outline: 2px solid #ffffff; outline-offset: 1px; }

.linea-alcance, .nota-dato {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 8px 16px 0;
  font-size: 12px;
  color: #4a5a78;
  line-height: 1.4;
}
.linea-alcance-icono, .nota-dato-icono { font-size: 16px; color: #185FA5; flex-shrink: 0; }
.nota-dato { background: #F2F7FD; border-radius: 8px; padding: 8px 10px; }
.nota-dato-pie { margin: 8px 16px 0; }
.nota-dato-ok { background: #EAF6EC; color: #1F5B2A; }
.nota-dato-icono-ok { font-size: 16px; color: #2E7D32; flex-shrink: 0; }
/* En ámbar y no en rojo: no es un error, es que falta dato o sobra lista. */
.nota-dato-alerta { background: #FDF4E3; color: #6B4A10; }
.nota-dato-icono-alerta { font-size: 16px; color: #9A6411; flex-shrink: 0; }

.titulares {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 16px 0;
}
.titular {
  flex: 1 1 200px;
  background: #F8FAFC;
  border-radius: 8px;
  padding: 10px 12px;
}
.titular-rotulo {
  margin: 0;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #64748b;
}
.titular-cifra { margin: 4px 0 0; font-size: 20px; font-weight: 700; color: #012653; }
.titular-cifra-alerta { color: #C0392B; }
.titular-nota { margin: 2px 0 0; font-size: 11px; color: #64748b; line-height: 1.4; }
.titular-nota-aviso { color: #8E2B20; }

.recuento-sugerencias { display: flex; flex-wrap: wrap; gap: 6px; margin: 10px 16px 0; }
.chip {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
  background: #F0F4F8;
  color: #64748b;
}
.chip-ok { background: #D1E7DD; color: #0F5132; }
.chip-aviso { background: #FDF4E3; color: #6B4A10; }
.chip-neutro { background: #F0F4F8; color: #64748b; }
/* Los abonos cuyo conjunto no se ha podido calcular: no es un cero, es un «no se sabe» (#537). */
.chip-alerta { background: #FDEDEE; color: #7A1B1B; }

.bloque { margin: 14px 16px 0; }
.bloque-titulo {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #012653;
}
.bloque-explica { margin: 2px 0 6px; font-size: 11px; color: #64748b; line-height: 1.4; }

/* En azul y no en ámbar: el ámbar es «no se sabe» —lo que dice `aviso-operacion-dudosa`— y esto es
   lo contrario, algo que el servidor ha dicho que NO se hizo. Dos cosas distintas no se pintan
   igual, porque entonces la que importa deja de distinguirse. */
.aviso-sin-aplicar {
  margin-top: 8px;
  padding: 8px 10px;
  background: #EEF4FB;
  border: 1px solid #BFD4EA;
  border-radius: 8px;
}
.aviso-sin-aplicar .regla-detalle { color: #12365F; }

.tabla { width: 100%; border-collapse: collapse; font-size: 11px; }
.tabla th {
  background: #1a2744;
  color: #c8d3e5;
  font-weight: 600;
  padding: 6px 8px;
  text-align: left;
}
.tabla td { padding: 5px 8px; border-bottom: 1px solid #eef2f7; vertical-align: top; }
.tabla tfoot td { background: #F8FAFC; font-weight: 700; border-bottom: 0; }
.tabla .num { text-align: right; }
.tabla-reparto { max-width: 420px; }

.aviso-error {
  background: #FDECEA;
  border-radius: 8px;
  color: #8E2B20;
  font-size: 13px;
}

.campo {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  box-sizing: border-box;
}
.campo-corto { width: 150px; }
.campo-rotulo {
  margin: 10px 0 4px;
  font-size: 11px;
  font-weight: 600;
  color: #4a5a78;
  display: block;
}
.campo-ayuda { margin: 4px 0 0; font-size: 11px; color: #64748b; line-height: 1.4; }
.campo-ayuda-aviso { color: #8E2B20; }

/* El estado DESCONOCIDO de una escritura vive en `aviso-operacion-dudosa.vue`, con su estilo
   dentro: era el mismo bloque en tres sitios de esta pantalla y es lo que se repartía. */

/* Un formulario bloqueado se tiene que VER bloqueado: si no, se escribe en él y no se entiende
   por qué no cambia nada. Mismo criterio que el `:disabled` de los botones. */
.form-bloqueado { opacity: 0.6; }
.campo-check { display: block; margin: 10px 0 0; font-size: 12px; color: #1a2744; }
.campo-inline { display: block; }
.fila-campos { display: flex; flex-wrap: wrap; gap: 14px; }

.opciones { display: flex; flex-wrap: wrap; gap: 6px; }
.opcion {
  width: auto;
  display: inline-block;
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #ffffff;
  color: #64748b;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
}
.opcion-elegida { background: #185FA5; color: #ffffff; border-color: #185FA5; }
.opcion:focus-visible { outline: 2px solid #185FA5; outline-offset: 2px; }

.regla {
  margin: 8px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  background: #ffffff;
}
.regla-pausada { opacity: 0.6; }
.regla-cabecera { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #1a2744; }
.regla-cabecera strong { flex: 1; min-width: 0; }
.regla-punto { width: 8px; height: 8px; border-radius: 50%; background: #94a3b8; flex-shrink: 0; }
.regla-punto-activa { background: #22c55e; }
.regla-detalle { margin: 4px 0 0; font-size: 11px; color: #64748b; line-height: 1.4; }
.regla-botones { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.btn-pequeno {
  width: auto;
  display: inline-block;
  font-size: 10px;
  padding: 4px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #ffffff;
  color: #4a5a78;
  cursor: pointer;
}
.btn-pequeno-azul { background: #185FA5; color: #ffffff; border-color: #185FA5; }
.btn-pequeno-rojo { color: #dc3545; border-color: #fecaca; }
.btn-pequeno:focus-visible { outline: 2px solid #185FA5; outline-offset: 2px; }
/* Deshabilitado se tiene que VER deshabilitado: si no, la pulsación que no hace nada se lee como
   que la pantalla se ha quedado colgada y se vuelve a pulsar. */
.btn-pequeno:disabled { opacity: 0.5; cursor: default; }

.botonera { display: flex; gap: 8px; }
</style>

<script>
import SelloDatos from '../../components/SelloDatos.vue';
import TablaAbonos from './tabla-abonos-cobros.vue';
import AvisoOperacionDudosa from './aviso-operacion-dudosa.vue';
import FactCobro from '../../js/db/FactCobro.js';
import Utils from '../../js/Utils.js';
import carrilPeticiones from '../../js/utils/carrilPeticiones.js';
import avisoNoGuardado from '../../js/utils/avisoNoGuardado.js';
import operacionesPendientes from '../../js/utils/operacionesAbonosCobros.js';

/**
 * Las frecuencias que entiende el planificador de reportes que ya existe
 * (`NotificacionScheduler.debeEjecutar`). Están aquí porque son parte del contrato: con otro valor,
 * la regla se guarda y no se ejecuta nunca, y eso no da ningún error en ninguna parte.
 *
 * `PUNTUAL` no está a propósito: el planificador la trata como «no ejecutar nunca», así que
 * ofrecerla sería ofrecer una programación que no programa nada. Para mandarlo una vez está
 * «Enviar reporte», que es lo que hace eso.
 */
const FRECUENCIAS = ['DIARIA', 'SEMANAL', 'MENSUAL'];

/**
 * Los siete días, porque el planificador ejecuta los siete (`NotificacionScheduler.DIAS_MAP`) y la
 * pantalla de notificaciones de OEA ya los ofrece: recortar aquí el fin de semana sería recortar
 * funcionalidad que ya hay, y Administración trabaja la cartera también en sábado.
 */
const DIAS_SEMANA = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO', 'DOMINGO'];

const FIN_DE_SEMANA = ['SABADO', 'DOMINGO'];

/**
 * Cada cuántos minutos pasa el envío automático: es el paso del reloj del servidor
 * (`NotificacionScheduler`, cada 5 minutos) y lo que la API acepta en la hora de una programación.
 *
 * Está aquí arriba, con las demás constantes de la periodicidad, y no abajo con las funciones
 * auxiliares: lo de abajo se evalúa después del componente y `const` no se puede leer antes de
 * inicializarse.
 */
const PASO_MINUTOS = 5;

/**
 * Si esa hora es una que el envío automático respeta.
 *
 * El reloj del servidor pasa cada `PASO_MINUTOS` minutos y da por buena la pasada cuando se queda a
 * menos de eso, así que unas `08:03` salen en la pasada de las **08:00** —antes de su hora— y la
 * marca del último envío tapa ya la de las 08:05. La API lo rechaza con un 400; esto es para decirlo
 * aquí, junto al campo, en vez de dejar que se guarde y vuelva el rechazo.
 */
function horaQueSeEjecutaASuHora(hora) {
  const partes = String(hora || '').split(':');
  if (partes.length !== 2) return false;
  const minutos = Number(partes[1]);
  return Number.isInteger(minutos) && minutos % PASO_MINUTOS === 0;
}

/**
 * El día de la semana escrito como se escribe en español.
 *
 * Los valores vienen del planificador sin tildes (`SABADO`, `MIERCOLES`), así que un
 * `toLowerCase()` a secas deja «sabado» en pantalla.
 */
const DIAS_ESCRITOS = {
  LUNES: 'lunes', MARTES: 'martes', MIERCOLES: 'miércoles', JUEVES: 'jueves',
  VIERNES: 'viernes', SABADO: 'sábado', DOMINGO: 'domingo'
};

function nombreDia(dia) {
  return DIAS_ESCRITOS[dia] || String(dia || '').toLowerCase();
}

/** Cuántos envíos del historial se enseñan: los últimos, que son los que se están mirando. */
const ENVIOS_QUE_SE_ENSENAN = 20;

/**
 * **Los tres sitios de esta pantalla donde puede quedar una escritura en el aire.**
 *
 * Son constantes y no cadenas sueltas porque viajan a `localStorage` y vuelven después de recargar:
 * el ámbito es lo que dice a qué parte de la pantalla pertenece un aviso recuperado. Con el texto
 * escrito a mano en cada sitio, un apunte guardado por una versión y leído por otra se quedaría sin
 * casa y el aviso no se pintaría en ninguna parte.
 *
 * El de las reglas es un **prefijo**: cada regla tiene el suyo, porque una operación sobre la de los
 * lunes no tiene por qué bloquear la de los viernes.
 */
const AMBITO_ENVIO = 'ENVIO';
const AMBITO_FORM = 'FORM';
const AMBITO_REGLA = 'regla:';

/**
 * **Los ámbitos que tienen un borrador delante**, y por tanto los que la proyección mantiene al día.
 *
 * Son estos dos y no los de las reglas: una operación sobre una regla no tiene campos propios que
 * rellenar —lo que se enseña de ella es su ficha, que se dibuja de la lista del servidor—, así que no
 * hay nada que proyectar. Los cerrojos de las reglas salen del registro igual que los de aquí, pero
 * por `computed` y no por asignación, que es lo que no hace falta sincronizar nunca.
 *
 * Es una lista y no dos `if` repartidos a propósito: el día que haya un tercer sitio con campos, se
 * añade aquí y la proyección ya lo cubre. Repartido es como volvió el defecto cuatro rondas seguidas.
 */
const AMBITOS_CON_BORRADOR = [AMBITO_ENVIO, AMBITO_FORM];

/**
 * Las situaciones que contesta la API para una operación **que esta pantalla tiene que distinguir**,
 * y la que es solo de la pantalla.
 *
 * `POR_CONSULTAR` no viene del servidor: es «hay una duda y todavía no se le ha preguntado». Está
 * aquí para que no haya ningún momento en el que la pantalla afirme algo sin haberlo preguntado.
 *
 * La API contesta cinco y aquí solo están nombradas tres, y no es un olvido: `EN_CURSO` y
 * `RETOMABLE` no necesitan ninguna rama. Las dos quieren decir «esto no ha terminado», así que la
 * duda se queda con su clave y las dos salidas de siempre —repetir la misma operación o volver a
 * preguntar—, y lo que cambia entre ellas es solo el texto, que lo redacta el servidor. Nombrar aquí
 * una situación que no decide nada sería una constante que nadie lee.
 */
/**
 * **Los tres estados de un apunte del registro**, que es lo que dice qué bloquea y qué ocupa cada
 * control. Ver `operaciones` en los datos del componente.
 *
 * Tres y no dos porque hay tres situaciones distintas y mezclar dos de ellas es el defecto que esto
 * cierra: `REPETIBLE` ya no bloquea nada pero sigue teniendo un borrador que es el único con el que
 * repetir la operación, así que no se puede tratar como si no existiera.
 */
const EN_VUELO = 'EN_VUELO';
const EN_DUDA = 'EN_DUDA';
const REPETIBLE = 'REPETIBLE';

const SITUACION_POR_CONSULTAR = 'POR_CONSULTAR';
const SITUACION_NO_CONSTA = 'NO_CONSTA';
const SITUACION_DESCONOCIDO = 'DESCONOCIDO';
const SITUACION_HECHA = 'HECHA';

/**
 * Los códigos HTTP que `DatabaseModel.sendRequest` trata como **timeout con el cambio quizá
 * aplicado** (ver su `errorCallback`), incluido el **408**, que es un `4xx` y aun así no es un
 * rechazo.
 *
 * Esta lista es la de allí a propósito: si se consultaran por separado, una de las dos acabaría
 * diciendo que un 408 es un rechazo y descartaría la clave de una escritura que podía estar hecha.
 */
const TIMEOUTS_CON_CAMBIO_QUIZA_APLICADO = [0, 408, 504, 520, 522, 524, 598, 599];

/**
 * **Cuánto se le deja a una petición para llegar al servidor antes de creerse un `NO_CONSTA`.**
 *
 * El servidor contesta por lo que tiene apuntado, y apunta la operación **al recibir el POST**
 * (`FactCobroAbonosOperaciones.reservar`). Así que un `NO_CONSTA` dice «ahora mismo no me consta», y
 * eso no es lo mismo que «no llegó»: si el POST todavía va de camino —otra pestaña acaba de
 * lanzarlo, o la petición salió y se cortó la respuesta— la consulta puede adelantársele, porque van
 * por conexiones distintas y nada ordena una respecto a la otra.
 *
 * Pasado este margen sí se puede afirmar: una petición que fuera a llegar ya habría llegado, y lo
 * que no, no va a llegar nunca —la pestaña que la lanzó la abortó al cerrarse—. Veinte segundos es
 * holgado para lo primero y poco para lo segundo, y equivocarse por holgura no cuesta nada: las dos
 * salidas del aviso siguen ahí desde el primer momento, y «Volver a intentarlo» repite la MISMA
 * clave, que el servidor no aplica dos veces.
 */
const MARGEN_HASTA_QUE_PUDO_LLEGAR = 20000;

/** Lo que se le dice de una operación recuperada mientras se le pregunta al servidor qué pasó. */
const TEXTO_RECUPERADA = 'Esta operación se quedó a medias antes de recargar la pantalla y no se '
  + 'sabe si llegó a aplicarse. Se está comprobando con el servidor; si no se puede, «Volver a '
  + 'intentarlo» repite LA MISMA operación y el servidor no la aplica dos veces.';

/**
 * Por qué sigue bloqueado cuando el servidor dice que **todavía** no le consta. §9: el motivo, quién
 * lo desbloquea y dónde, junto al control.
 *
 * Y se enseña este texto en vez del que manda el servidor a propósito: el suyo dice «no se ha
 * aplicado nada, así que se puede volver a intentar», que es una afirmación correcta de lo que él
 * sabe y falsa de lo que está pasando cuando el POST aún no le ha llegado.
 */
const NO_CONSTA_PUEDE_ADELANTARSE = 'El servidor dice que de momento no le consta esa operación, '
  + 'pero su petición puede estar todavía de camino —otra pestaña acaba de lanzarla, o salió sin que '
  + 'llegara la respuesta—, y las dos van por conexiones distintas. Así que esto NO quiere decir que '
  + 'no se haya aplicado, y sigue bloqueado a propósito. Vuelve a pulsar «Comprobar cómo quedó» en '
  + 'unos segundos: si entonces sigue sin constarle, es que no llegó. O pulsa «Volver a intentarlo», '
  + 'que repite LA MISMA operación y el servidor no la aplica dos veces.';

/** Por qué sigue bloqueado cuando no se ha podido preguntar. §9: el motivo, junto al control. */
const NO_SE_PUDO_COMPROBAR = 'No se ha podido preguntar al servidor cómo quedó, así que esto sigue '
  + 'bloqueado a propósito: darlo por no hecho dejaría repetirlo a ciegas y podría quedar dos '
  + 'veces. Vuelve a pulsar «Comprobar cómo quedó» cuando haya conexión, o «Volver a intentarlo», '
  + 'que repite la misma operación y el servidor no la duplica.';

/** El formulario en blanco: el estado del que se parte y al que vuelve «Limpiar». */
function formEnBlanco() {
  return {
    id: null,
    /** El `modificadaEn` de la regla que se está editando; en un alta no hay nada que repetir. */
    visto: null,
    nombre: '',
    frecuencia: 'SEMANAL',
    hora: '08:00',
    diaSemana: 'LUNES',
    diaMes: 1,
    soloLaborables: true,
    emailsExtra: '',
    activa: true
  };
}

/**
 * La huella de lo que hay escrito en el formulario, para poder distinguir «abierto» de «abierto y
 * tocado».
 *
 * Va campo a campo sobre los de `formEnBlanco` y no con un `JSON.stringify` del objeto entero: el
 * orden de las claves de un literal no es parte de ningún contrato, y una comparación que dependa
 * de él diría que hay cambios sin guardar justo después de poner el formulario.
 */
function huellaForm(form) {
  return Object.keys(formEnBlanco()).map(function(campo) {
    const valor = form ? form[campo] : null;
    return valor === null || valor === undefined ? '' : String(valor);
  }).join('\u0001');
}

/** Una lista de correos de un campo de texto: sin blancos y sin vacíos. */
function correosDe(texto) {
  if (!texto) return [];
  return String(texto).split(',')
    .map(function(email) { return email.trim(); })
    .filter(function(email) { return !!email; });
}

/**
 * El informe **Abonos abiertos** (COB-7, #193): la lista de trabajo de Administración.
 *
 * **No escribe nada del negocio.** Lo único que escribe esta pantalla son las reglas de envío, que
 * viven en la funcionalidad de reportes de siempre; ningún abono y ninguna factura cambian por
 * abrirla, listarla, enviarla o programarla (D-COB-9). La columna de facturas candidatas es una
 * sugerencia y lo dice con palabras.
 *
 * **Las cifras y los textos los manda la API.** El importe (D-COB-2), el corte de «más de un año»,
 * qué es una candidata y el reparto por año vienen ya calculados y ya redactados: aquí no se vuelve
 * a decidir nada de eso. Es lo que hace que el correo del informe y esta pantalla no puedan decir
 * cosas distintas, porque los dos pintan el mismo DTO.
 *
 * **Las dos listas vienen ya separadas** por el servidor (`masDeUnAnio` y `restoDeAbonos`): la regla
 * de los 365 días es una, y filtrando aquí serían dos.
 *
 * **Y toda escritura de aquí pasa por `ejecutar`, con su clave de operación.** Es la decisión
 * técnica de @Donchurru del 15/10/2026 (opciones D + C), después de tres rondas de revisión sobre la
 * misma avería en un sitio distinto cada vez: cuando una respuesta se perdía, la pantalla
 * **deducía** qué había pasado mirando otra lista. Las tres piezas que lo cierran:
 *
 *  1. **un solo camino** — las seis operaciones (el envío directo, el alta, la edición,
 *     pausar/activar, borrar y «Enviar ahora») pasan por `ejecutar` y `lanzarOperacion`, así que
 *     cualquier garantía que se añada vale para las seis. Teniendo cada una su `then`/`catch`, cada
 *     arreglo se quedaba en la que lo había destapado.
 *  2. **la clave sobrevive a la recarga** (opción C) — se apunta en el navegador ANTES de lanzar,
 *     con la identidad entera y el borrador, y al volver a entrar se recupera y se pregunta. Antes
 *     vivía solo aquí dentro y un F5 se la llevaba.
 *  3. **el resultado se pregunta, no se deduce** (opción D) — `FactCobro.estadoOperacionAbonos` le
 *     pregunta al servidor por esa operación. Lo que se miraba antes no podía contestar: la lista de
 *     programaciones no dice nada de un envío, y «Últimos envíos» **no contiene los envíos
 *     directos**.
 *
 * Y lo que esta pantalla NO hace, que era la condición: no reenvía nada por su cuenta al recuperar
 * una sesión, y no se inventa una clave nueva para esquivar un resultado desconocido.
 */
export default {
  components: { SelloDatos, TablaAbonos, AvisoOperacionDudosa },
  data: function() {
    return {
      cargando: false,
      error: null,
      sinPermiso: false,
      dto: null,
      /**
       * **EL REGISTRO DE LAS OPERACIONES DE ESCRITURA DE ESTA PANTALLA**, por su clave de operación:
       * `{ '<clave>': { entrada, cuando, estado, texto, situacion, … } }`.
       *
       * Es el **único** sitio donde se apunta que algo está a medias, y de aquí se deducen las tres
       * cosas que antes se llevaban por separado —y que por eso se contradecían—:
       *
       *  - **si un control está cerrado** (`sinResolverDe`): lo está mientras el ámbito tenga algo
       *    `EN_VUELO` o `EN_DUDA`. Antes eran dos banderas booleanas y un hueco por regla, así que
       *    **dos** operaciones a la vez del mismo sitio compartían una sola bandera: cuando
       *    contestaba la primera se soltaba el cerrojo con la segunda todavía viajando.
       *  - **quién ocupa el control** (`quienOcupa`): el apunte más antiguo que quede del ámbito, se
       *    resuelva lo que se resuelva y en el orden que sea. Antes lo decidía cada resolución por su
       *    cuenta, así que dos respuestas que llegaran al revés dejaban delante el borrador de una y
       *    vaciaban el de la otra.
       *  - **qué avisos se pintan** (`dudasAbiertas`, `dudaDe`, `sinAplicar`): los de estado
       *    `EN_DUDA` y los `REPETIBLE`. Y el que va **junto a un control** es el de quien lo ocupa
       *    y de nadie más: con filtros distintos para el borrador y para el aviso, los dos salían
       *    de esta misma cola y podían señalar operaciones distintas.
       *
       * Y DE AQUÍ SE DEDUCEN SOLAS, que es la decisión técnica de @Donchurru del 16/10/2026
       * (planteamiento **B**). Antes el borrador que ocupa cada ámbito se deducía igual de bien, pero
       * **había que acordarse de pedirlo** con una llamada detrás de cada camino, y la ronda 4
       * encontró el camino que no se acordaba —el éxito de un envío cerraba la ventana sin mirar si
       * quedaba otra entrada ocupándola—. Mientras la garantía dependa de que nadie se olvide,
       * siempre queda un sitio olvidado. Ahora **toda** incorporación, actualización,
       * resolución, retirada, recuperación o cambio de otra pestaña pasa por `cambiarRegistro`, y es
       * esa transición la que proyecta: ningún llamante tiene que acordarse de nada.
       *
       * Los tres estados, y por qué hacen falta tres:
       *
       *  - `EN_VUELO` — la petición ha salido y no ha vuelto. Cierra el control.
       *  - `EN_DUDA` — la respuesta se perdió y no se sabe si se aplicó. Cierra el control y pinta
       *    su aviso con las salidas.
       *  - `REPETIBLE` — el servidor ha acreditado que **no** se aplicó nada (`NO_CONSTA`). No cierra
       *    nada —volver a pulsar es justo lo que toca— pero **sigue en la cola** para que lo que
       *    quedó escrito siga delante hasta que alguien lo repita o lo tire con «Limpiar».
       *
       * Solo cuatro métodos lo escriben —`apuntarEnVuelo`, `apuntarEnDuda`, `apuntarSinAplicar` y
       * `olvidarOperacion`— y los cuatro pasan por `cambiarRegistro`, que es por donde se entra.
       */
      operaciones: {},
      FRECUENCIAS: FRECUENCIAS,
      DIAS_SEMANA: DIAS_SEMANA,
      envio: {
        abierto: false,
        error: null,
        emails: '',
        asunto: ''
      },
      programacion: {
        abierto: false,
        cargando: false,
        error: null,
        editando: false,
        lista: [],
        form: formEnBlanco(),
        /**
         * La huella del formulario **tal como se puso**. Comparada con la de ahora dice si alguien
         * ha escrito algo que no está guardado, que es lo único que permite no descartarlo en
         * silencio al abrir otra regla (ver `formTocado`).
         *
         * La apunta `ponerForm`, que es el único sitio que cambia el formulario sin que lo escriba
         * una persona: si alguna asignación se lo saltara, esta huella se quedaría vieja y la
         * pantalla diría que hay cambios sin guardar que no existen.
         */
        huella: huellaForm(formEnBlanco()),
        /**
         * El historial de envíos: lo que cuenta si un envío **programado** salió o no.
         *
         * Su fallo se guarda aparte del de las reglas: que no se pueda leer el historial no dice
         * nada de las programaciones, y un solo aviso para los dos haría creer que no hay ninguna.
         *
         * **No se usa para reconciliar una operación**, y eso es contrato: aquí no aparecen los
         * envíos de «Enviar reporte», así que no puede decir si uno de esos salió. Lo que reconcilia
         * es la consulta de la operación (`FactCobro.estadoOperacionAbonos`).
         */
        envios: { lista: [], error: null }
      }
    };
  },
  created: function() {
    const self = this;
    // Un carril por lectura: el botón de recargar puede lanzar varias seguidas y las respuestas no
    // vuelven en orden. Sin esto, la vieja llega después y pinta encima de la buena.
    //
    // #472 · Y el «cargando» de cada una lo enciende y lo apaga su carril, que es el único que sabe
    // cuándo ha contestado la petición vigente: una respuesta descartada no lo apaga, y no hay un
    // `false` por manejador que olvidar.
    self.carrilInforme = carrilPeticiones({
      cargando: function(si) { self.cargando = si; }
    });
    self.carrilProgramaciones = carrilPeticiones({
      cargando: function(si) { self.programacion.cargando = si; }
    });
    self.carrilEnvios = carrilPeticiones();
    /**
     * **Qué operación tiene ahora mismo su borrador delante en cada ámbito**, por clave.
     *
     * Es la memoria de la proyección y el motivo de que proyectar no pueda tirar nada: lo escrito se
     * toca **solo cuando cambia quién ocupa el ámbito**. Sin esta memoria, cualquier cambio del
     * registro —el envío de una regla, por ejemplo— volvería a proyectar el formulario y vaciaría un
     * alta a medias que nunca llegó a salir, que no es de ninguna operación y es de quien está
     * delante.
     *
     * Arranca con un `null` por ámbito y no vacío: un `undefined` se leería como «esto ha cambiado»
     * la primera vez y vaciaría los campos sin que ninguna operación lo hubiera pedido.
     *
     * No va en `data` porque no se pinta: es contabilidad de la proyección, igual que los carriles.
     */
    self.proyectado = {};
    AMBITOS_CON_BORRADOR.forEach(function(ambito) { self.proyectado[ambito] = null; });
    /**
     * Cuántas transiciones del registro hay abiertas. Solo la de fuera proyecta: un cambio compuesto
     * —soltar lo que ya no ocupa y apuntar lo que sale ahora— es **un** cambio, y proyectar en medio
     * dejaría ver un ocupante intermedio que nunca existió.
     */
    self.transicionesAbiertas = 0;
    // Lo que cambie otra pestaña entra por el mismo sitio que todo lo demás.
    self.escucharOtrasPestanas();
  },
  beforeDestroy: function() {
    this.dejarDeEscucharOtrasPestanas();
  },
  computed: {
    /**
     * **Todas las operaciones de esta pantalla cuyo resultado no se sabe**, en una sola lista y las
     * más antiguas primero.
     *
     * Es lo que pinta el aviso de arriba, y existe porque quien acaba de recargar no sabe que hay
     * nada a medias ni tiene por qué abrir dos popups para enterarse. Aquí sale **todo** lo que hay
     * en `dudas`, también lo que no ocupa ahora mismo el hueco de su control: la lista es lo que
     * garantiza que una segunda duda del mismo sitio siga estando a la vista y se pueda resolver.
     */
    dudasAbiertas: function() {
      const self = this;
      return this.apuntes()
        .filter(function(duda) { return duda.estado === EN_DUDA; })
        .map(function(duda) {
          return {
            ambito: duda.entrada.ambito,
            que: conMayuscula(queEsDe(duda.entrada)),
            clave: duda.clave,
            // Si los campos que se ven en su parte de la pantalla son los de ESTA operación o los
            // de otra. Es la pregunta de quien va a pulsar «Volver a intentarlo» desde aquí: lo
            // que se repite es lo de esta operación, se vea o no.
            queSeVe: self.queSeVeDe(duda),
            duda: duda
          };
        });
    },
    /**
     * **Las operaciones que el servidor ha dicho que NO se aplicaron**, las más antiguas primero.
     *
     * Están resueltas —el servidor lo ha acreditado— pero no cerradas: lo que quedó escrito es lo
     * único con lo que repetirlas, y es de esa operación y de ninguna otra. Hasta ahora no se veían
     * en ninguna parte: quien resolvía dos operaciones del mismo sitio se encontraba delante el
     * borrador de una de ellas sin nada que dijera de dónde salía ni por qué estaba ahí, y el aviso
     * que lo contaba era un mensaje que ya se había ido de la pantalla.
     *
     * Así que se enseñan, con lo que son y con las salidas que tienen: repetirla cuando se puede, o
     * tirar lo que quedó escrito. Es lo que pedía la decisión del 16/10/2026: «un `NO_CONSTA` que
     * requiere decisión o corrección no se borra de la interfaz al resolverse otra operación».
     *
     * Aquí caen las dos maneras de que el servidor acredite que no se aplicó nada —un `NO_CONSTA` de
     * la consulta y un rechazo claro al lanzar—, porque para la cola y para el cerrojo son la misma
     * cosa. Lo que no es igual es la salida, y por eso cada una lleva la suya: con el rechazo la
     * clave está gastada y lo que toca es corregir y guardar.
     */
    sinAplicar: function() {
      const self = this;
      return this.apuntes()
        .filter(function(apunte) { return apunte.estado === REPETIBLE; })
        .map(function(apunte) {
          return {
            clave: apunte.clave,
            ambito: apunte.entrada.ambito,
            que: conMayuscula(queEsDe(apunte.entrada)),
            sePuedeRepetir: apunte.sePuedeRepetir !== false,
            porQue: apunte.sePuedeRepetir !== false
              ? 'El servidor ha confirmado que no llegó a aplicarse, así que nada ha cambiado.'
                + ' «Volver a intentarlo» repite LA MISMA operación.'
              : 'El servidor la ha rechazado y no ha cambiado nada. Corrige lo que te haya dicho y'
                + ' vuelve a guardar: eso será una operación nueva, porque esta ya está cerrada.',
            // Si es la que tiene su borrador delante se dice, y si no es ella también: es la
            // pregunta que se hace quien ve los campos rellenos —de qué es esto que tengo
            // escrito—, y con dos operaciones del mismo sitio no se puede adivinar. El mismo
            // rótulo que en «Operaciones sin resolver», del mismo sitio: dos textos distintos para
            // la misma pregunta es como acaban contando cosas distintas.
            queSeVe: self.queSeVeDe(apunte),
            apunte: apunte
          };
        });
    },
    /** La duda que ocupa el aviso de «Enviar reporte», o `null`. */
    dudaEnvio: function() {
      return this.dudaDe(AMBITO_ENVIO);
    },
    /** La duda que ocupa el aviso del formulario de la programación, o `null`. */
    dudaForm: function() {
      return this.dudaDe(AMBITO_FORM);
    },
    /** De qué es lo que hay escrito en «Enviar reporte», cuando es de una que no se aplicó. */
    delanteEnvio: function() {
      return this.loQueHayDelanteDe(AMBITO_ENVIO);
    },
    /** Lo mismo en el formulario de la programación. */
    delanteForm: function() {
      return this.loQueHayDelanteDe(AMBITO_FORM);
    },
    /** Por qué «Enviar reporte» sigue cerrado cuando el aviso que lo cierra está arriba (§9). */
    cerradoFueraEnvio: function() {
      return this.motivoCerradoFuera(AMBITO_ENVIO);
    },
    /** Lo mismo para el formulario de la programación. */
    cerradoFueraForm: function() {
      return this.motivoCerradoFuera(AMBITO_FORM);
    },
    /** Si la pantalla está enseñando cifras ahora mismo. Gobierna el sello de datos (#16). */
    hayCifras: function() {
      return !!this.dto && !this.cargando;
    },
    totales: function() {
      return (this.dto && this.dto.totales) || {};
    },
    masDeUnAnio: function() {
      return (this.dto && this.dto.totalesMasDeUnAnio) || {};
    },
    resto: function() {
      return (this.dto && this.dto.totalesResto) || {};
    },
    sinFecha: function() {
      return (this.dto && this.dto.totalesSinFechaEmision) || {};
    },
    /** Si hay algún abono sin fecha de emisión; solo entonces se avisa de ellos. */
    haySinFecha: function() {
      return Number(this.sinFecha.abonos) > 0;
    },
    reparto: function() {
      return (this.dto && this.dto.porAnioEmision) || [];
    },
    hayDestinatarios: function() {
      return correosDe(this.envio.emails).length > 0;
    },
    /**
     * Si el formulario de la programación está bloqueado: mientras se guarda y mientras el
     * resultado del guardado siga sin saberse.
     *
     * Las dos cosas en un solo sitio y no repartidas por campo, por lo mismo que `bloqueoDeRegla`:
     * cada vez que este cerrojo se ha repartido, el control de al lado se ha quedado fuera. Si
     * mañana hay un campo más, pregunta aquí y ya está cubierto.
     *
     * El segundo caso es el que pidió @Donchurru: con el resultado en duda, dejar el formulario
     * editable invita a cambiarlo y a volver a guardar, y eso sería una operación distinta sobre
     * algo que puede estar ya escrito.
     */
    formBloqueado: function() {
      return this.sinResolverDe(AMBITO_FORM).length > 0;
    },
    /** Lo mismo para «Enviar reporte»: mientras sale y mientras no se sepa si salió. */
    envioBloqueado: function() {
      return this.sinResolverDe(AMBITO_ENVIO).length > 0;
    },
    /**
     * Si el formulario tiene **algo en vuelo ahora mismo**. Es solo el rótulo del botón
     * («Guardando…»): quien decide si se puede pulsar es `formBloqueado`, que cuenta además las
     * operaciones cuyo resultado no se sabe.
     */
    guardandoAhora: function() {
      return this.enVueloDe(AMBITO_FORM).length > 0;
    },
    /** Lo mismo para el botón de «Enviar reporte». */
    enviandoAhora: function() {
      return this.enVueloDe(AMBITO_ENVIO).length > 0;
    },
    /**
     * Si hay algo escrito en el formulario que todavía no se ha guardado: un alta a medias, o una
     * edición con los campos ya cambiados.
     *
     * Existe porque lo que hay escrito **no se tira en silencio**. Los dos sitios que lo tiraban
     * —«Editar» en otra regla y volver a abrir el popup— no tenían manera de distinguir un
     * formulario recién abierto de uno con media hora de trabajo dentro.
     */
    formTocado: function() {
      return huellaForm(this.programacion.form) !== this.programacion.huella;
    },
    /**
     * Si la regla se puede guardar: hace falta nombre, destinatarios y el día que pida su
     * frecuencia. Sin esto se guardaría una regla que no manda nada a nadie y nadie sabría por qué.
     */
    programacionGuardable: function() {
      return !this.motivoNoGuardable;
    },
    /** Por qué no se puede guardar todavía. Se enseña junto al botón, no al principio (§9). */
    motivoNoGuardable: function() {
      const form = this.programacion.form;
      if (!form.nombre || !form.nombre.trim()) {
        return 'Ponle un nombre en «Nombre», arriba: es con lo que se reconoce en esta lista.';
      }
      if (!correosDe(form.emailsExtra).length) {
        return 'Escribe al menos un correo en «Destinatarios», arriba: una programación sin '
          + 'destinatarios no manda nada.';
      }
      if (!form.hora) {
        return 'Pon la hora a la que se manda, en «Hora».';
      }
      if (this.avisoHora) {
        return this.avisoHora;
      }
      if (form.frecuencia === 'SEMANAL' && !form.diaSemana) {
        return 'Elige el día de la semana.';
      }
      if (this.avisoFinDeSemana) {
        return this.avisoFinDeSemana;
      }
      if (form.frecuencia === 'MENSUAL' && !diaDelMesValido(form.diaMes)) {
        return 'El día del mes tiene que estar entre 1 y 28, para que exista en febrero también.';
      }
      return null;
    },
    /**
     * El envío automático pasa cada cinco minutos, así que una hora con otros minutos sale **antes**
     * de la que pone: las 08:03 salen a las 08:00. Se dice **junto al campo Hora** —que es donde se
     * arregla— y no solo junto a «Guardar» (§9), igual que el aviso del fin de semana.
     */
    avisoHora: function() {
      const hora = this.programacion.form.hora;
      if (!hora || horaQueSeEjecutaASuHora(hora)) {
        return null;
      }
      return 'La hora va de ' + PASO_MINUTOS + ' en ' + PASO_MINUTOS + ' minutos —08:00, 08:05,'
        + ' 08:10…—: el envío automático solo pasa cada ' + PASO_MINUTOS + ' minutos, así que a las '
        + hora + ' el informe saldría antes de esa hora. Ajusta los minutos aquí arriba.';
    },
    /**
     * El sábado y el domingo con «Solo días laborables» puesto se descartan la una a la otra: el
     * planificador tira la regla por laborable antes de mirar el día elegido, así que no saldría
     * ningún día. Se dice **junto a la casilla** —que es el control que lo desbloquea— y no solo
     * junto al botón de guardar (§9).
     */
    avisoFinDeSemana: function() {
      const form = this.programacion.form;
      if (form.frecuencia !== 'SEMANAL' || !form.soloLaborables
          || FIN_DE_SEMANA.indexOf(form.diaSemana) < 0) {
        return null;
      }
      return 'Has elegido ' + nombreDia(form.diaSemana) + ', que no es laborable: con «Solo días'
        + ' laborables» marcado el informe no saldría ningún día. Desmarca la casilla de aquí abajo'
        + ' para programarlo en fin de semana.';
    }
  },
  methods: {
    onPageInit: function() {
      this.cargar();
      // Antes de nada: lo que quedó a medias de antes de recargar. Se consulta, no se reenvía.
      this.recuperarOperaciones();
    },
    /** Pide el informe. */
    cargar: function() {
      const self = this;
      self.error = null;
      self.carrilInforme.lanzar(FactCobro.abonosAbiertos(), {
        exito: function(respuesta) {
          self.sinPermiso = false;
          self.dto = respuesta;
        },
        limpiar: function() {
          // Sin cifras no se deja lo de antes en pantalla: el sello de datos estaría afirmando algo
          // sobre unos datos que esta pantalla ya no enseña.
          self.dto = null;
        },
        fallo: function(e) {
          self.sinPermiso = !!e && e.status === 403;
          self.error = self.sinPermiso
            ? 'El informe de abonos abiertos es de Administración, Directores y Administrador.'
            : 'No se ha podido cargar el informe. Vuelve a intentarlo con el botón de recargar.';
        }
      });
    },

    // =============================================== recuperar lo que quedó a medias (opción C)

    /**
     * **Lo que quedó a medias antes de recargar, recuperado y consultado.**
     *
     * Opción **C** de la decisión de @Donchurru del 15/10/2026. Antes la clave de una operación
     * vivía solo en el estado del componente, así que una recarga, una pestaña cerrada o un «ir
     * atrás» a mitad de un guardado se llevaban por delante lo único que permitía preguntar después
     * qué había pasado: al volver, la pantalla se enseñaba como si no hubiera nada pendiente y lo
     * siguiente que se pulsara iba a ciegas.
     *
     * **Se consulta, no se reenvía.** Reenviar al arrancar sería mandar un correo que nadie ha
     * pulsado, y lo prohíbe la decisión con esas palabras. Lo que se hace es poner el aviso en su
     * sitio —con lo escrito de vuelta, para poder repetir la misma operación— y preguntarle al
     * servidor cómo acabó cada una.
     */
    recuperarOperaciones: function() {
      const self = this;
      const usuario = usuarioDeAhora();
      // Sin saber quién está delante no se recupera nada: los apuntes llevan dentro el borrador de
      // quien los dejó, y enseñárselos a otra persona sería enseñarle sus destinatarios.
      if (!usuario) return;
      const recuperadas = [];
      // **Las incorporaciones son UN cambio del registro**, no uno por apunte: así la proyección
      // decide quién ocupa cada ámbito con la cola entera delante y no con la mitad que haya entrado.
      self.cambiarRegistro(function() {
        operacionesPendientes.pendientesDe(usuario).forEach(function(entrada) {
          if (!entrada.ambito || !entrada.clave) return;
          // Lo que esta pestaña ya tiene apuntado NO se toca. Al abrir la pantalla el registro está
          // vacío y esto no hace nada, pero esta misma recuperación es la que atiende lo que cambie
          // otra pestaña: ahí sí hay operaciones propias dentro, y volver a apuntarlas en duda
          // bajaría a `EN_DUDA` algo que está en vuelo y cuya respuesta va a llegar.
          if (self.operaciones[entrada.clave]) return;
          // La duda se pinta YA, antes de que vuelva la consulta: lo que no puede pasar es que la
          // pantalla se enseñe desbloqueada mientras la pregunta viaja. Y van TODAS, también dos del
          // mismo ámbito: cada una con su clave, que es lo que hace falta para poder resolverlas.
          recuperadas.push(self.apuntarEnDuda(entrada, TEXTO_RECUPERADA));
        });
      });
      // Y la consulta DESPUÉS de cerrar la transición, nunca dentro: lo que conteste el servidor
      // vuelve a entrar por `cambiarRegistro`, y abrirla desde dentro de otra dejaría la proyección
      // de la de fuera decidiendo con la cola a medio formar.
      recuperadas.forEach(function(duda) { self.comprobar(duda); });
    },
    /**
     * **Lo que otra pestaña cambia en el registro entra por la misma transición que todo lo demás.**
     *
     * Hace falta porque los apuntes viven en `localStorage`, que es del navegador y no de la pestaña:
     * dos pestañas de este informe comparten registro. Si una resuelve una operación y la otra no se
     * entera, la segunda sigue enseñando un aviso de algo ya cerrado y con su control bloqueado; y al
     * revés, lo que una deje a medias la otra no lo sabe.
     *
     * Lo condiciona la decisión del 16/10/2026 con esas palabras: el cambio de otra pestaña **pasa
     * por el registro y su transición común**. No se toca aquí ningún campo de la pantalla.
     */
    escucharOtrasPestanas: function() {
      const self = this;
      // Fuera del navegador no hay nada que escuchar —el arnés de pruebas monta este componente en
      // Node—, y registrarlo dos veces atendería cada cambio dos veces.
      if (typeof window === 'undefined' || !window.addEventListener) return;
      if (self.alCambiarOtraPestana) return;
      self.alCambiarOtraPestana = function(evento) {
        const clave = evento && evento.key;
        // El navegador avisa de TODAS las claves que cambian. Quién es de este registro lo contesta
        // el módulo que las nombra, no una cadena copiada aquí. Un `key` vacío es un `clear()`: ahí
        // sí hay que mirar, porque puede haberse llevado los apuntes.
        if (clave && !operacionesPendientes.esSuya(clave)) return;
        self.alCambiarElRegistroDeOtraPestana();
      };
      window.addEventListener('storage', self.alCambiarOtraPestana);
    },
    dejarDeEscucharOtrasPestanas: function() {
      if (typeof window === 'undefined' || !window.removeEventListener) return;
      if (!this.alCambiarOtraPestana) return;
      window.removeEventListener('storage', this.alCambiarOtraPestana);
      this.alCambiarOtraPestana = null;
    },
    /**
     * Otra pestaña ha tocado el registro: se incorpora lo que haya dejado y **se pregunta** por lo
     * que ya no está.
     *
     * Las dos mitades, y ninguna adivina nada:
     *
     *  - lo apuntado que aquí no estaba entra como una recuperación, con su aviso y su consulta;
     *  - lo que aquí sigue en duda y ya no está apuntado es que **la otra pestaña lo resolvió**. No
     *    se da por bueno ni se tira: se le pregunta al servidor por esa operación, que es lo único
     *    que lo sabe, y es su respuesta la que cierra el aviso.
     */
    alCambiarElRegistroDeOtraPestana: function() {
      const self = this;
      const usuario = usuarioDeAhora();
      if (!usuario) return;
      // **Qué hay apuntado AHORA, antes de tocar nada.** Lo que falte es la señal de que la otra
      // pestaña lo resolvió, y lo primero que hace cualquier transición es reponer el apunte de lo
      // que esta pestaña tiene sin resolver (`conservarApuntes`): leyéndolo después, esa señal ya se
      // habría borrado y la duda se quedaría aquí para siempre sin que nadie preguntase.
      //
      // Reponerlo y preguntar no se contradicen, y es el reparto de la decisión del 15/10: la falta
      // del apunte no acredita nada —quien acredita es el servidor— pero la clave hay que
      // conservarla mientras no conteste.
      const apuntadas = {};
      operacionesPendientes.pendientesDe(usuario).forEach(function(entrada) {
        apuntadas[entrada.clave] = true;
      });
      self.recuperarOperaciones();
      self.apuntes().forEach(function(apunte) {
        if (apunte.estado !== EN_DUDA || apuntadas[apunte.clave]) return;
        self.comprobar(apunte);
      });
    },
    /**
     * Devuelve al formulario lo que se estaba mandando, para que repetir la operación sea repetir
     * lo mismo y para que quien vuelve vea de qué le están hablando.
     *
     * No es decoración: el aviso dice «no se sabe si se ha aplicado el alta de X», y sin el
     * formulario delante no hay manera de saber qué era X.
     */
    devolverLoEscrito: function(entrada) {
      const borrador = entrada.borrador || {};
      if (entrada.ambito === AMBITO_ENVIO) {
        this.envio.emails = (borrador.emails || []).join(', ');
        this.envio.asunto = borrador.asunto || '';
        return;
      }
      if (entrada.ambito === AMBITO_FORM) {
        this.ponerForm(Object.assign(formEnBlanco(), {
          id: borrador.id || null,
          visto: borrador.visto === undefined ? null : borrador.visto,
          nombre: borrador.nombre || '',
          frecuencia: borrador.frecuencia || 'SEMANAL',
          hora: borrador.hora || '08:00',
          diaSemana: borrador.diaSemana || 'LUNES',
          diaMes: borrador.diaMes || 1,
          soloLaborables: borrador.soloLaborables !== false,
          emailsExtra: (borrador.emailsExtra || []).join(', '),
          activa: borrador.activa !== false
        }), !!borrador.id);
      }
    },
    /** Abre el popup donde vive ese aviso, desde la lista de operaciones sin resolver. */
    abrirDondeVive: function(abierta) {
      if (abierta.ambito === AMBITO_ENVIO) {
        this.abrirEnvio();
        return;
      }
      this.abrirProgramaciones();
    },

    // ============================== una escritura de esta pantalla, con su clave, en un solo sitio

    /**
     * **Toda escritura de esta pantalla pasa por aquí**, y es lo que cierra la familia que llevó
     * tres rondas de revisión: «soltar la garantía cuando el resultado es desconocido».
     *
     * Las seis operaciones —el envío directo, el alta, la edición, pausar/activar, borrar y «Enviar
     * ahora»— hacían lo mismo con su propia copia del `then`/`catch`, así que cada arreglo se
     * quedaba en el camino que lo había destapado y la ronda siguiente traía el de al lado. Aquí
     * están las cuatro garantías juntas, una sola vez:
     *
     * 1. **la clave se apunta en el navegador ANTES de lanzar** — si la pestaña se cierra ahora, la
     *    operación se puede recuperar y preguntar; apuntándola al volver la respuesta, el único caso
     *    que importa sería el único que no quedaría apuntado;
     * 2. **la respuesta perdida no se interpreta** — se deja la duda con su clave dentro, y las
     *    salidas son repetir la misma operación o preguntarle al servidor;
     * 3. **el rechazo claro sí se puede afirmar** — un `4xx` que no sea un timeout es «te he
     *    rechazado y no he escrito nada», así que el apunte se olvida y lo siguiente es una
     *    operación nueva;
     * 4. **lo que sale bien se olvida** — ya no hay nada que reconciliar.
     *
     * Y la primera es una **condición para lanzar**, no un apunte de cortesía: si el navegador no
     * puede guardar la clave, la petición NO sale. Ver `noSePudoApuntar`.
     *
     * @param entrada `{ambito, usuario, accion, entidad, clave, borrador}`. La construye `operacion`.
     */
    ejecutar: function(entrada) {
      const self = this;
      // **Cuándo sale este intento**, apuntado antes de guardar para que viaje también al navegador:
      // es lo que permite después distinguir «al servidor no le consta» de «al servidor no le consta
      // TODAVÍA, porque el POST va de camino». Se pone aquí, que es por donde pasan las seis
      // operaciones, y no en cada una. Ver `noConstaEstaAcreditado`.
      entrada.intentadaEn = Date.now();
      // Si la clave no queda guardada, no se manda nada: una operación cuya clave no sobrevive a una
      // recarga no se puede reconciliar después, y el intento siguiente llevaría otra clave.
      if (!operacionesPendientes.guardar(entrada)) {
        self.noSePudoApuntar(entrada);
        return;
      }
      // El aviso de lo que no salió deja de valer en cuanto sale otra cosa.
      if (entrada.ambito === AMBITO_ENVIO) self.envio.error = null;
      // **Soltar lo que ya no ocupa y apuntar lo que sale ahora son UN cambio del registro.** Entre
      // las dos cosas el ámbito puede quedarse un instante sin nadie, y proyectar ahí vaciaría los
      // campos para volver a rellenarlos a continuación.
      self.cambiarRegistro(function() {
        // Una operación NUEVA se manda con lo que hay escrito delante, así que lo que quedaba a
        // mano de una ya cerrada —un `NO_CONSTA` que se podía repetir— deja la cola: si no, al
        // terminar esta volvería a aparecer delante el borrador de aquella.
        //
        // Un REINTENTO no, y es la misma familia que el aviso descolocado: lo que repite es SU
        // borrador, que puede no ser el que está delante, y el de la que está delante no ha pedido
        // tirarlo nadie —es de una operación que el servidor dijo que no se aplicó, así que es lo
        // único con lo que repetirla—. Se distingue por si la clave ya está en el registro, que es
        // exacto: `operar` genera una nueva por operación y `reintentar` reutiliza la que hay.
        if (!self.operaciones[entrada.clave]) {
          self.olvidarRepetiblesDe(entrada.ambito, entrada.clave);
        }
        self.apuntarEnVuelo(entrada);
      });
      lanzarOperacion(entrada).then(function(respuesta) {
        operacionesPendientes.olvidar(entrada.clave);
        self.olvidarOperacion(entrada.clave);
        self.alSalirBien(entrada, respuesta);
      }).catch(function(e) {
        if (resultadoEnDuda(e)) {
          // No se afirma que no se haya hecho: puede haberse hecho y haberse perdido la respuesta.
          // El apunte del navegador se CONSERVA, que es lo que hace que esto sobreviva a un F5.
          self.apuntarEnDuda(entrada, textoEnDuda(e, queEsDe(entrada)));
          // Informativo: para que se vea cómo ha quedado la lista. NO reconcilia nada —eso lo hace
          // la consulta de la operación—, que es justo lo que se hacía mal.
          if (entrada.ambito !== AMBITO_ENVIO) self.cargarProgramaciones();
          return;
        }
        // Un rechazo claro es «no he escrito nada». El apunte del navegador se suelta —no hay nada
        // que reconciliar y esa clave ya está gastada: el servidor no la acepta con otro contenido—
        // pero la operación **sigue en la cola** y deja de bloquear.
        //
        // Sigue por lo mismo que un `NO_CONSTA`: lo que quedó escrito es suyo y es lo que hay que
        // corregir. Sacándola de la cola, el ámbito se quedaba vacío y la proyección vaciaba los
        // campos, que es justo lo contrario de lo que pide un rechazo (#164: si el formulario se
        // limpia sin decir nada, la regla parece guardada y no manda ningún informe).
        operacionesPendientes.olvidar(entrada.clave);
        self.apuntarSinAplicar(entrada.clave, false);
        self.alRechazo(entrada, e);
      });
    },
    /**
     * **La operación no se ha lanzado**, porque su clave no se ha podido guardar en el navegador.
     *
     * Pasa cuando el almacenamiento está lleno o bloqueado —modo privado, un navegador con las
     * cookies y el almacenamiento desactivados— o cuando ya hay veinte operaciones de esta persona
     * sin resolver. En los dos casos lanzar sería peor que no lanzar: la clave viviría solo en esta
     * pestaña, así que una recarga la perdería y el intento siguiente llevaría otra, que el servidor
     * no puede reconocer como reintento. Eso es cómo salen dos programaciones iguales o dos correos.
     *
     * Lo que se dice lleva las tres cosas de §9 y **junto al control**: qué no se ha hecho, por qué,
     * y qué desbloquea la situación.
     */
    noSePudoApuntar: function(entrada) {
      const porQue = 'No se ha hecho ' + queEsDe(entrada) + ', y no se ha mandado ninguna petición.'
        + ' Esta pantalla necesita guardar un identificador de la operación en este navegador para'
        + ' poder saber después si se aplicó, y no ha podido: resuelve las que tengas en «Operaciones'
        + ' sin resolver», o permite el almacenamiento del navegador para este sitio (en modo privado'
        + ' no se puede), y vuelve a intentarlo.';
      if (entrada.ambito === AMBITO_ENVIO) {
        this.envio.error = porQue;
        return;
      }
      this.$f7.dialog.alert(porQue, 'No se ha hecho nada');
    },
    /**
     * Arma la identidad de una operación y la ejecuta.
     *
     * La **identidad completa** es lo que pidió la decisión: usuario, acción, entidad, clave y el
     * borrador con el que se puede repetir. Y la clave se genera **aquí y una sola vez por
     * operación**: un reintento no vuelve a pasar por este método, pasa por `reintentar`, que
     * reutiliza la que ya hay. Es lo que impide «generar otra clave para esquivar un resultado
     * desconocido».
     */
    operar: function(ambito, accion, entidad, borrador) {
      this.ejecutar({
        ambito: ambito,
        usuario: usuarioDeAhora(),
        accion: accion,
        entidad: entidad || null,
        clave: nuevaOperacion(),
        borrador: borrador
      });
    },
    /** Qué se le dice a quien está delante cuando la operación ha salido bien. */
    alSalirBien: function(entrada, respuesta) {
      if (entrada.accion === 'ENVIO') {
        // **El éxito se anuncia de SU operación, pero no cierra la ventana si queda otra entrada
        // ocupando «Enviar reporte»**, y es el hallazgo de la ronda 4: cerrar aquí se llevaba de la
        // vista el aviso y los campos de la segunda —que seguía bloqueando el botón— y lo que
        // quedaba delante al reabrir eran los destinatarios del correo YA enviado. Pulsar «Enviar»
        // ahí es un segundo correo al mismo buzón con una clave nueva, que el servidor no reconoce.
        //
        // Quién ocupa la ventana después de esto lo decide la proyección de `cambiarRegistro`; aquí
        // solo se mira si queda alguien, que es lo que esta respuesta sí puede afirmar.
        const sigueOcupado = this.colaDe(AMBITO_ENVIO).length > 0;
        if (!sigueOcupado) this.envio.abierto = false;
        // El número lo dice el servidor y es el de buzones a los que SALIÓ el correo, que no tiene
        // por qué ser el de la casilla: en modo pruebas los destinatarios se sustituyen por los de
        // test. Si no llega número no se inventa con la longitud de la lista —sería anunciar algo
        // que nadie ha medido—: se dice que salió y ya.
        const cuantos = Number(respuesta);
        this.aviso((Number.isFinite(cuantos) && cuantos > 0
          ? 'Informe enviado a ' + cuantos + (cuantos === 1 ? ' destinatario.' : ' destinatarios.')
          : 'Informe enviado.')
          // Y se dice por qué la ventana se queda abierta, junto a lo que se acaba de anunciar: si
          // no, se lee como que el envío no ha terminado.
          + (sigueOcupado
            ? ' Queda otra operación de «Enviar reporte» sin resolver, así que la ventana se'
              + ' queda abierta con lo suyo delante.'
            : ''));
        return;
      }
      if (entrada.accion === 'ENVIO_REGLA') {
        // El estado llega EN UNA RESPUESTA CORRECTA: un `ERROR` no es un fallo de la llamada, es el
        // resultado del envío. Darlo por bueno sin leerlo anunciaría como enviado algo que no salió.
        const estado = typeof respuesta === 'string' ? respuesta : (respuesta && respuesta.dto);
        this.aviso(estado === 'ENVIADO'
          ? 'Informe enviado.'
          : 'El envío ha terminado con estado ' + (estado || 'desconocido')
            + '. Mira el historial de envíos.');
      }
      // Lo que queda delante del formulario no se decide aquí: la operación ya ha salido del
      // registro y de ahí sale la proyección. Si queda otra cosa en su cola —se ha reintentado desde
      // «Operaciones sin resolver» la segunda de dos—, lo que hay delante es el borrador de esa, y es
      // lo único que dice de qué habla su aviso.
      this.cargarProgramaciones();
    },
    /**
     * Qué se le dice cuando el servidor ha RECHAZADO la operación.
     *
     * Aquí sí se puede afirmar que no se ha escrito nada —lo dice él con un `4xx`—, y el apunte ya
     * se ha olvidado, así que lo que se mande después de corregir es una operación nueva.
     */
    alRechazo: function(entrada, e) {
      if (entrada.ambito === AMBITO_ENVIO) {
        // El popup se queda abierto con lo escrito: si se cerrara, parecería enviado y no lo está.
        this.envio.error = mensajeDeFallo(e, 'No se ha podido enviar el informe.');
        return;
      }
      // #164 · el formulario se queda con lo escrito: si se limpiara sin decir nada, la regla
      // parecería guardada y no lo estaría, y no se mandaría ningún informe.
      avisoNoGuardado(this.$f7, queEsDe(entrada), e);
      if (entrada.ambito !== AMBITO_FORM) this.cargarProgramaciones();
    },

    // ======================================= resolver una duda: reintentar, preguntar, dar por vista

    /**
     * Repite **la misma** operación, con su misma clave: el servidor la reconoce y no la aplica dos
     * veces.
     *
     * No se ofrece con el resultado `DESCONOCIDO` —el servidor no la repite nunca, así que solo
     * daría otra vez la misma respuesta— y por eso se comprueba también aquí y no solo en el
     * `v-if` del botón: entre la pulsación y el repintado hay un hueco.
     */
    reintentar: function(duda) {
      if (!duda || duda.consultando || duda.situacion === SITUACION_DESCONOCIDO) return;
      // Y tampoco con la clave gastada: un rechazo claro la cierra, y el servidor no la acepta otra
      // vez con otro contenido. Se comprueba aquí y no solo en el `v-if` del botón, igual que lo
      // demás: entre la pulsación y el repintado hay un hueco.
      if (duda.sePuedeRepetir === false) return;
      // Y **de una en una por ámbito**. Dos dudas del mismo sitio salen las dos en «Operaciones sin
      // resolver», así que sus dos botones se pueden pulsar seguidos; lanzadas a la vez, la respuesta
      // de la primera soltaba el cerrojo con la segunda en vuelo y dejaba escribir una tercera cosa
      // encima, que la respuesta que faltaba podía deshacer. El motivo se dice junto al botón
      // (`porQueNoSeRepite`, §9) y además aquí, porque entre la pulsación y el repintado hay un
      // hueco.
      if (this.porQueNoSeRepite(duda)) return;
      this.ejecutar(duda.entrada);
    },
    /**
     * Por qué «Volver a intentarlo» de esa duda no se puede pulsar ahora mismo, o `null`.
     *
     * Las tres cosas de §9 juntas y **junto al control**: qué no se puede hacer, por qué, y qué lo
     * desbloquea. Un botón que no hace nada al pulsarlo se lee como pantalla colgada.
     */
    porQueNoSeRepite: function(duda) {
      if (!duda) return null;
      const enVuelo = this.enVueloDe(duda.entrada.ambito).filter(function(apunte) {
        return apunte.clave !== duda.clave;
      });
      if (!enVuelo.length) return null;
      return 'Espera a que el servidor conteste a ' + queEsDe(enVuelo[0].entrada) + ': las'
        + ' operaciones del mismo sitio se repiten de una en una, porque lanzadas a la vez la'
        + ' respuesta de una puede deshacer lo de la otra. El botón vuelve en cuanto conteste.';
    },
    /**
     * **Le pregunta al servidor cómo acabó esa operación**, que es lo único que lo sabe.
     *
     * Esto sustituye al «Descartarlo» de antes, y la diferencia es el defecto que arregla: antes se
     * recargaba una lista y, si la lectura salía bien, se daba la duda por resuelta. Pero la lista
     * de programaciones no dice nada de un envío y el historial de «Últimos envíos» **no contiene
     * los envíos directos**, así que esa comprobación no podía responder la pregunta que se le
     * hacía. Ahora se consulta el registro de operaciones (opción D de la decisión del 15/10/2026).
     *
     * Y la duda se suelta **solo con la finalización acreditada**: terminada, o sin efecto. Con «en
     * curso» o «no se sabe» el aviso se queda, porque soltarlo es desbloquear una escritura nueva.
     */
    comprobar: function(duda) {
      const self = this;
      if (!duda || duda.consultando) return;
      duda.consultando = true;
      duda.noSePudo = null;
      // Lo que conteste el servidor se escribe en el registro, así que entra por la transición como
      // todo lo demás: es la condición de la decisión del 16/10/2026 —«evitar escrituras al registro
      // desde callbacks que eludan esa transición»—. Y la finalización lleva la identidad exacta: lo
      // que se escribe es `duda`, nunca «la que esté delante», así que una respuesta tardía solo
      // cambia su entrada.
      FactCobro.estadoOperacionAbonos(duda.clave).then(function(estado) {
        self.cambiarRegistro(function() {
          duda.consultando = false;
          // La respuesta tiene que ser de ESTA operación: con el formulario y dos reglas en duda a la
          // vez, una respuesta sin identificar se aplicaría a la que estuviera delante.
          if (!estado || estado.operacion !== duda.clave || !estado.situacion) {
            duda.noSePudo = NO_SE_PUDO_COMPROBAR;
            return;
          }
          duda.situacion = estado.situacion;
          // El texto lo manda la API: es una afirmación sobre lo que el servidor sabe y sobre lo que
          // no sabe, no una frase de pantalla.
          if (estado.texto) duda.texto = estado.texto;
          duda.detalle = detalleDe(duda.entrada, estado);
          if (estado.situacion === SITUACION_HECHA) {
            self.resolver(duda, estado);
            return;
          }
          // Un `NO_CONSTA` cierra la duda **solo si acredita el final**, y no lo acredita cuando la
          // petición todavía puede estar de camino: ahí lo único que dice es que no le consta
          // todavía. Ver `noConstaEstaAcreditado`.
          if (estado.situacion === SITUACION_NO_CONSTA) {
            if (self.noConstaEstaAcreditado(duda)) {
              self.resolver(duda, estado);
              return;
            }
            duda.texto = NO_CONSTA_PUEDE_ADELANTARSE;
          }
        });
      }).catch(function() {
        self.cambiarRegistro(function() {
          duda.consultando = false;
          duda.noSePudo = NO_SE_PUDO_COMPROBAR;
        });
      });
    },
    /**
     * **Si un `NO_CONSTA` acredita que no se aplicó nada, o solo que al servidor no le consta
     * TODAVÍA.**
     *
     * El servidor apunta la operación al recibir el POST, así que contesta `NO_CONSTA` por dos
     * motivos que para él son el mismo —no llegó, o se rechazó sin escribir— y hay un tercero que no
     * puede distinguir: **que esté llegando ahora**. La consulta y la escritura van por conexiones
     * distintas y nada ordena una respecto a la otra, así que la consulta puede adelantarse al POST.
     *
     * Tomarlo por terminal ahí es el fallo, y lo caro no es el aviso equivocado: `resolver` suelta el
     * apunte del navegador —se pierde la única clave con la que se podía preguntar— y **desbloquea el
     * ámbito**, así que lo siguiente que se pulse sale con una clave NUEVA que el servidor no
     * reconoce como reintento. Eso es un segundo correo, o dos programaciones iguales, que es
     * exactamente lo que todo este registro existe para que no pase.
     *
     * Las dos razones por las que la petición puede estar de camino, y las dos están aquí y no
     * repartidas por quien pregunta:
     *
     *  - **está en vuelo**, diga lo que diga el reloj: no ha vuelto, así que por definición puede
     *    estar llegando. Hoy no se pregunta por nada en vuelo —los avisos son de lo que está en duda—
     *    pero el día que se pregunte, la garantía ya está puesta;
     *  - **el intento salió hace poco**: menos de {@link MARGEN_HASTA_QUE_PUDO_LLEGAR}. Es el caso de
     *    la otra pestaña, que apunta en el navegador y lanza a continuación: el aviso del navegador
     *    llega aquí ANTES de que su POST llegue al servidor.
     *
     * Se mide con `intentadaEn` —cuándo salió este intento— y no con `cuando` —cuándo empezó la
     * operación—, porque un reintento mueve lo primero y no lo segundo: con `cuando`, una operación
     * que se repite media hora después pasaría por antigua con su petición recién salida. Y sin
     * ningún instante se trata como antigua, por lo mismo que la ventana del servidor
     * (`FactCobroAbonosOperaciones.dentroDeLaVentana`): un apunte al que le falta su instante no se
     * puede dar por reciente, y esperar por él sería esperar para siempre.
     */
    noConstaEstaAcreditado: function(apunte) {
      if (!apunte) return false;
      if (apunte.estado === EN_VUELO) return false;
      const intento = (apunte.entrada && apunte.entrada.intentadaEn) || apunte.cuando;
      if (!intento) return true;
      return Date.now() - intento >= MARGEN_HASTA_QUE_PUDO_LLEGAR;
    },
    /**
     * Cierra una duda cuya **finalización está acreditada** y desbloquea su control.
     *
     * Los dos casos son terminales y el servidor los distingue: `HECHA` es «se hizo, y esto es lo
     * que dio» y `NO_CONSTA` es «no se aplicó nada» —el `NO_CONSTA` que llega aquí, que es el que ya
     * no puede adelantarse a su propio POST: lo filtra `noConstaEstaAcreditado`, porque esto suelta
     * el apunte del navegador y el ámbito—. En el segundo, lo escrito se queda: lo que
     * corresponde es poder volver a pulsar, y la operación siguiente será nueva porque esta ya está
     * cerrada.
     */
    resolver: function(duda, estado) {
      const self = this;
      operacionesPendientes.olvidar(duda.clave);
      self.cambiarRegistro(function() {
        if (estado.situacion === SITUACION_HECHA) {
          // Aplicada: no queda nada que reconciliar ni que repetir, así que sale de la cola.
          self.olvidarOperacion(duda.clave);
          return;
        }
        // No se aplicó nada. Deja de bloquear, pero **sigue en la cola**: lo que se escribió es lo
        // único con lo que repetirla, y es suyo. Lo que decide si está delante es el orden de la
        // cola y no el de las respuestas, que es lo que se contradecía. Y aquí la clave sigue
        // sirviendo —el servidor no aplicó nada con ella—, así que sí se puede repetir igual.
        self.apuntarSinAplicar(duda.clave, true);
      });
      // Y lo que queda delante lo ha puesto ya la proyección de esa transición: de quien ocupe AHORA
      // el control, por orden de cola y no por orden de respuestas.
      if (estado.situacion === SITUACION_HECHA) {
        self.aviso('Esa operación sí se aplicó' + (duda.detalle ? ' — ' + duda.detalle : '')
          + '. No hace falta repetirla.');
      } else {
        // Y se dice dónde se queda, porque el aviso de esta línea se va de la pantalla en segundos y
        // lo que quedó escrito sigue ahí: en «Sin aplicar» está con su nombre y sus dos salidas.
        self.aviso('No se aplicó nada. Lo que habías escrito se queda en «Sin aplicar», arriba,'
          + ' para repetirlo o descartarlo.');
      }
      self.cargarProgramaciones();
    },
    /**
     * Tira lo que quedó escrito de una operación que el servidor acreditó como **no aplicada**.
     *
     * Es la segunda salida de «Sin aplicar» y la pulsa una persona: nada se tira solo. No hay nada
     * que olvidar en el navegador —`resolver` ya soltó su apunte cuando el servidor dijo que no se
     * había aplicado nada—, así que esto solo la saca de la cola, y la proyección deja delante lo de
     * quien ocupe el ámbito después.
     */
    descartarSinAplicar: function(apunte) {
      if (!apunte || apunte.estado !== REPETIBLE) return;
      this.olvidarOperacion(apunte.clave);
    },
    /**
     * Da por vista una operación de resultado **desconocido**, que es la única que no se resuelve
     * preguntando: el servidor no va a poder decir más.
     *
     * La pulsa una persona a sabiendas y nunca se hace sola. Y en el formulario **se vacía lo
     * escrito**: dejarlo relleno con «Guardar» a mano es invitar a mandar lo mismo con una clave
     * nueva sobre algo que puede estar ya guardado, que es exactamente lo que la decisión del
     * 15/10/2026 prohíbe. Volver a darlo de alta tiene que ser deliberado.
     */
    darPorVisto: function(duda) {
      if (!duda || duda.consultando || duda.situacion !== SITUACION_DESCONOCIDO) return;
      operacionesPendientes.olvidar(duda.clave);
      // Sale de la cola entera: puede estar aplicada, así que no se deja a mano para repetirla.
      // Volver a darla de alta tiene que ser deliberado. Y lo que queda delante —lo de quien ocupe
      // ahora el control, o nada— lo pone la proyección de esta transición.
      this.olvidarOperacion(duda.clave);
      this.aviso('Dada por vista. Puede haberse aplicado: compruébalo en la lista antes de'
        + ' repetirla.');
      this.cargarProgramaciones();
    },
    // ========================== EL REGISTRO: la transición única, lo que apunta y lo que proyecta

    /**
     * **LA TRANSICIÓN ÚNICA DEL REGISTRO**: todo cambio pasa por aquí, y al cerrarse proyecta.
     *
     * Es el planteamiento **B** que eligió @Donchurru el 16/10/2026, después de cuatro rondas de
     * revisión sobre la misma familia —«varias operaciones a la vez en el mismo ámbito»—. La ronda 3
     * ya había juntado en un solo sitio la cuenta de lo que cada parte de la pantalla tiene a medias,
     * y funcionaba; lo que no cerraba la familia es que **cada camino tenía que acordarse de pedir**
     * la sincronización después, y la ronda 4 encontró el camino que no se acordaba. Mientras la
     * garantía dependa de que nadie se olvide, siempre queda un sitio olvidado.
     *
     * Aquí no hay nada que recordar: el único modo de tocar el registro es pasar por esta función, y
     * de ella sale la proyección. Un camino nuevo que apunte algo queda cubierto por existir.
     *
     * **Y un cambio compuesto es UN cambio.** Las transiciones se anidan —`apuntarEnDuda` llama a
     * `apuntarEnVuelo`, `ejecutar` suelta y apunta— y solo la de fuera proyecta: proyectar en medio
     * dejaría ver un ocupante intermedio que nunca existió, y en el formulario eso es un vaciado y un
     * rellenado seguidos.
     *
     * El `finally` no es adorno: si la mutación revienta, la proyección tiene que correr igual, o el
     * contador se queda alto y a partir de ahí ningún cambio volvería a proyectar nada.
     *
     * **Y de aquí salen las DOS cosas que el registro gobierna**, no solo lo que se ve: lo que se ve
     * (`proyectar`) y lo que queda guardado en el navegador (`conservarApuntes`). Ver el por qué en
     * esa segunda, que es el hallazgo de la ronda 3 del 16/10.
     */
    cambiarRegistro: function(mutar) {
      this.transicionesAbiertas += 1;
      try {
        return mutar();
      } finally {
        this.transicionesAbiertas -= 1;
        if (this.transicionesAbiertas === 0) {
          // El apunte antes que los campos: así, si no se ha podido reponer, el aviso que se pinta a
          // continuación ya lo dice.
          this.conservarApuntes();
          this.proyectar();
        }
      }
    },
    /**
     * **LO QUE EL REGISTRO TIENE SIN RESOLVER, SIGUE APUNTADO EN EL NAVEGADOR.** Una sola vez, para
     * todos los cambios y todos los finales.
     *
     * Es la misma familia que cerró el planteamiento B —«que la garantía viva en un sitio por el que
     * pasen todos»— aplicada a la otra mitad del registro. B puso aquí los cerrojos y el borrador que
     * se ve; el **apunte del navegador** se seguía escribiendo y borrando en cada camino, y por eso
     * volvió a salir en el sitio de al lado:
     *
     *  - la ronda 1 de esta tanda: un `NO_CONSTA` que podía adelantarse al POST **soltaba** el apunte
     *    de una operación que seguía sin resolverse;
     *  - la ronda 3: una carrera entre pestañas **se llevaba** el apunte —A termina bien y olvida la
     *    clave que B tiene en vuelo, porque es la misma— y la rama que recibía el resultado ambiguo
     *    de B solo restauraba la duda en memoria. Al recargar B, la clave ya no estaba.
     *
     * Las dos acaban igual: la operación sigue sin resolverse y su clave ya no está, así que una
     * recarga la pierde y lo siguiente que se pulse crea otra operación —otro correo, otra
     * programación—. Que es lo único que todo esto existe para impedir.
     *
     * LA REGLA, Y ES UNA: **si el registro lo tiene en vuelo o en duda, está apuntado.** No hace
     * falta que ningún camino se acuerde, porque todos pasan por la transición.
     *
     * Y VA EN UN SOLO SENTIDO: repone lo que falta y **nunca borra**. Borrar es afirmar que la
     * operación acabó, y eso solo lo puede decir el servidor o una persona —`resolver`,
     * `darPorVisto`, la respuesta buena, el rechazo claro—. Además, un apunte que a esta pestaña le
     * sobra puede ser de una operación que otra está lanzando ahora mismo: reponer de más solo cuesta
     * una pregunta al servidor, que contestará que terminó; borrar de más es la avería.
     */
    conservarApuntes: function() {
      this.apuntes().forEach(function(apunte) {
        if (apunte.estado !== EN_VUELO && apunte.estado !== EN_DUDA) return;
        if (operacionesPendientes.tieneApunte(apunte.clave)) {
          apunte.sinApuntar = false;
          return;
        }
        // Si el navegador no puede guardarlo no se puede hacer nada más —la petición ya salió— pero
        // sí se puede decir, y se dice junto al aviso de esa operación (§9).
        apunte.sinApuntar = !operacionesPendientes.reapuntar(apunte.entrada);
      });
    },
    /**
     * **LA PROYECCIÓN: de lo que hay en el registro a lo que se ve.** Una sola, para todos los
     * cambios y todos los finales.
     *
     * Los cerrojos y los avisos ya se deducían solos —son `computed` sobre el registro— y por eso
     * nunca se han quedado viejos. Lo que no es `computed` es el borrador: `envio.emails` y
     * `programacion.form` son campos que una persona escribe, así que hay que asignarlos, y asignar
     * es lo que había que acordarse de hacer. Esto lo hace una vez y para los dos.
     *
     * LA REGLA ES LA IDENTIDAD, Y ES LO QUE HACE QUE PROYECTAR NO PUEDA TIRAR NADA: lo escrito se
     * toca **solo cuando cambia quién ocupa el ámbito**. De ahí salen las dos garantías de la
     * decisión:
     *
     *  - un cambio del registro que no mueve al ocupante **no toca los campos**, así que un alta a
     *    medias que no es de ninguna operación —lo que alguien tiene escrito sin guardar— sobrevive a
     *    que se resuelva, se retire o se recupere cualquier otra cosa, incluso de otro ámbito;
     *  - quién ocupa sale del **orden de la cola** (`quienOcupa`, el más antiguo) y no del orden en
     *    que contesten las respuestas, así que dos que lleguen al revés dejan lo mismo delante que
     *    si llegaran en orden.
     *
     * Vaciar es «la cola de este ámbito se ha quedado vacía», nunca «esta resolución dice que vacíe»:
     * con lo segundo, un `NO_CONSTA` se llevaba por delante el borrador de la operación que el
     * servidor había dicho que **no** se aplicó, que era la única que había que repetir.
     */
    proyectar: function() {
      const self = this;
      AMBITOS_CON_BORRADOR.forEach(function(ambito) {
        const ocupa = self.quienOcupa(ambito);
        const clave = ocupa ? ocupa.clave : null;
        if (self.proyectado[ambito] === clave) return;
        self.proyectado[ambito] = clave;
        if (ocupa) {
          self.devolverLoEscrito(ocupa.entrada);
          return;
        }
        if (ambito === AMBITO_FORM) self.vaciarForm();
        if (ambito === AMBITO_ENVIO) self.vaciarEnvio();
      });
    },

    /**
     * Apunta que una operación **sale ahora**: entra en el registro —o vuelve a él, si es un
     * reintento— con el estado `EN_VUELO`.
     *
     * Vuelve a él **sin salir**, y eso es lo que importa: un reintento no borra el apunte para
     * volver a crearlo, le cambia el estado. Borrándolo primero había un instante en el que el
     * ámbito no tenía nada apuntado, y era justo el instante en el que se decidía si el control
     * estaba libre.
     *
     * Con `$set` porque la clave no existe al crearse el componente: en Vue 2 una propiedad añadida a
     * mano no es reactiva y el aviso no se pintaría.
     */
    apuntarEnVuelo: function(entrada) {
      const self = this;
      return self.cambiarRegistro(function() {
        const anterior = self.operaciones[entrada.clave];
        if (anterior) {
          anterior.estado = EN_VUELO;
          // Lo que contestó la vez anterior deja de valer: se está preguntando otra cosa.
          anterior.situacion = SITUACION_POR_CONSULTAR;
          anterior.consultando = false;
          anterior.noSePudo = null;
          anterior.detalle = null;
          // Y vuelve a poder repetirse: lo que la cerró la vez anterior ya no vale, porque esto es
          // otro intento de la misma operación.
          anterior.sePuedeRepetir = true;
          return anterior;
        }
        const apunte = apunteDe(entrada, EN_VUELO, null);
        self.$set(self.operaciones, entrada.clave, apunte);
        return apunte;
      });
    },
    /** Apunta que su respuesta se perdió: no se sabe si se aplicó, así que el control sigue cerrado. */
    apuntarEnDuda: function(entrada, texto) {
      const self = this;
      return self.cambiarRegistro(function() {
        const apunte = self.apuntarEnVuelo(entrada);
        apunte.estado = EN_DUDA;
        apunte.texto = texto;
        return apunte;
      });
    },
    /**
     * Apunta que el servidor ha acreditado que **no se aplicó nada**.
     *
     * No cierra el control —volver a pulsar es lo que toca— pero el apunte **se queda en la cola**:
     * lo que quedó escrito es lo único que permite repetirlo igual, y es de esta operación y de
     * ninguna otra. Sale de la cola cuando se lanza algo nuevo en ese ámbito o con «Limpiar».
     *
     * @param sePuedeRepetir si la MISMA operación se puede volver a lanzar con su misma clave.
     *        `true` con un `NO_CONSTA`: el servidor no la aplicó y la clave sigue sirviendo. `false`
     *        con un rechazo claro (`4xx`): esa clave está gastada —el servidor no la acepta con otro
     *        contenido—, así que lo que toca es corregir los campos y guardar, que es una operación
     *        nueva. Los dos casos son el mismo para la cola y para el cerrojo; lo que cambia es la
     *        salida que se ofrece, y ofrecer la que no vale manda a pulsar algo que no lleva a nada.
     */
    apuntarSinAplicar: function(clave, sePuedeRepetir) {
      const self = this;
      self.cambiarRegistro(function() {
        const apunte = self.operaciones[clave];
        if (!apunte) return;
        apunte.estado = REPETIBLE;
        apunte.sePuedeRepetir = sePuedeRepetir !== false;
      });
    },
    /** Saca una operación del registro: su final está acreditado y no queda nada que reconciliar. */
    olvidarOperacion: function(clave) {
      const self = this;
      if (!clave || !(clave in self.operaciones)) return;
      self.cambiarRegistro(function() { self.$delete(self.operaciones, clave); });
    },
    /** Todo lo apuntado, de lo más antiguo a lo más nuevo. */
    apuntes: function() {
      const operaciones = this.operaciones;
      return Object.keys(operaciones)
        .map(function(clave) { return operaciones[clave]; })
        .filter(function(apunte) { return !!apunte; })
        .sort(function(a, b) { return (a.cuando || 0) - (b.cuando || 0); });
    },
    /**
     * **La cola de un ámbito**: todo lo que tiene apuntado, del estado que sea, de lo más antiguo a
     * lo más nuevo.
     *
     * De cualquier estado a propósito: un `REPETIBLE` ya no bloquea nada, pero sigue en la cola
     * porque lo escrito delante es suyo.
     */
    colaDe: function(ambito) {
      if (!ambito) return [];
      return this.apuntes().filter(function(apunte) {
        return apunte.entrada.ambito === ambito;
      });
    },
    /** Lo que ese ámbito tiene **en vuelo** ahora mismo. */
    enVueloDe: function(ambito) {
      return this.colaDe(ambito).filter(function(apunte) { return apunte.estado === EN_VUELO; });
    },
    /**
     * **Lo que ese ámbito tiene sin resolver**: en vuelo, o con el resultado sin saber.
     *
     * Es el cerrojo de los dos controles grandes, en un solo sitio y **contando**, no con una
     * bandera: con dos operaciones a la vez del mismo sitio, la respuesta de la primera soltaba una
     * bandera compartida y dejaba escribir encima mientras la segunda seguía viajando.
     */
    sinResolverDe: function(ambito) {
      return this.colaDe(ambito).filter(function(apunte) {
        return apunte.estado === EN_VUELO || apunte.estado === EN_DUDA;
      });
    },
    /**
     * **Quién ocupa el control de ese ámbito**: el apunte más antiguo que quede, o `null`.
     *
     * El más antiguo y no el último, porque es el que tiene delante quien está mirando. Y se deduce
     * de la cola en vez de decidirlo cada resolución por su cuenta: dos respuestas que llegaran al
     * revés —la nueva primero— dejaban delante el borrador de una y vaciaban el de la otra.
     */
    quienOcupa: function(ambito) {
      const cola = this.colaDe(ambito);
      return cola.length ? cola[0] : null;
    },
    /** Lo que ese ámbito tiene **en duda**: resuelto no, y sin saber si se aplicó. */
    enDudaDe: function(ambito) {
      return this.colaDe(ambito).filter(function(apunte) { return apunte.estado === EN_DUDA; });
    },
    /**
     * **El aviso que va junto al control de ese ámbito**: el de la operación que lo ocupa, y solo
     * si está en duda. Si la que ocupa no está en duda, aquí no va ningún aviso.
     *
     * Es **el ocupante** y no «la más antigua en duda», y la diferencia es el defecto que esto
     * cierra: los dos salían de la misma cola con filtros distintos, así que podían ser
     * operaciones distintas. Con dos del mismo sitio y la primera resuelta como `NO_CONSTA` —que
     * no bloquea pero **se queda delante** con su borrador—, el ocupante era la primera y este
     * aviso el de la segunda: los campos de una junto al aviso y los botones de la otra. Quien
     * pulsa «Volver a intentarlo» ahí está convencido de repetir lo que ve.
     *
     * Las demás no se pierden de vista: están arriba, en «Operaciones sin resolver», cada una con
     * su aviso, sus salidas y el rótulo de si lo que se ve es lo suyo (`queSeVeDe`). Y lo que
     * mantiene cerrado el control no es este aviso, es `sinResolverDe`, que cuenta la cola entera;
     * por qué sigue cerrado cuando el aviso no está aquí lo dice `motivoCerradoFuera`, junto al
     * control (§9).
     */
    dudaDe: function(ambito) {
      const ocupa = this.quienOcupa(ambito);
      return ocupa && ocupa.estado === EN_DUDA ? ocupa : null;
    },
    /**
     * **Si los campos que se ven en ese ámbito son los de esta operación o los de otra**, con esas
     * palabras, o `null` cuando ese ámbito no tiene campos —las reglas no los tienen—.
     *
     * Lo piden los dos bloques de arriba, que están lejos de los campos: desde ahí se repite una
     * operación que puede no ser la que se ve, y sin decirlo se lee como que se manda lo de la
     * pantalla.
     */
    queSeVeDe: function(apunte) {
      if (!apunte || AMBITOS_CON_BORRADOR.indexOf(apunte.entrada.ambito) < 0) return null;
      // Se pregunta por la cola y no por la memoria de la proyección: es el mismo dato —la
      // proyección sale de ahí— pero la cola es reactiva y la memoria no, así que preguntándole a
      // la memoria el rótulo se quedaría viejo hasta el siguiente repintado.
      if (this.quienOcupa(apunte.entrada.ambito) === apunte) {
        return 'Es lo que hay escrito ahora en esa parte de la pantalla.';
      }
      return 'En esa parte de la pantalla se ven los datos de otra operación. «Volver a intentarlo»'
        + ' de aquí repite LO DE ESTA, no lo que se vea allí.';
    },
    /**
     * **De qué es lo que hay escrito en ese ámbito cuando no es de una duda**: de una operación
     * que el servidor acreditó como no aplicada y que sigue delante con su borrador. `null` si lo
     * que ocupa es una duda —de eso habla su propio aviso— o si no hay nada apuntado.
     *
     * Va junto a los campos porque es ahí donde se hace la pregunta, y «Sin aplicar» está arriba.
     */
    loQueHayDelanteDe: function(ambito) {
      const ocupa = this.quienOcupa(ambito);
      if (!ocupa || ocupa.estado !== REPETIBLE) return null;
      return 'Lo que hay escrito aquí es de ' + queEsDe(ocupa.entrada) + ', que el servidor ha'
        + ' dicho que NO se aplicó. Está arriba, en «Sin aplicar», con «Volver a intentarlo» y'
        + ' «Descartar lo escrito».';
    },
    /**
     * **Por qué este control sigue cerrado cuando su aviso no está aquí**, o `null`.
     *
     * Pasa con dos operaciones del mismo sitio: la que ocupa los campos está resuelta como no
     * aplicada y la que cierra el control es otra, cuyo aviso está arriba. Sin esto el control se
     * quedaría mudo, que es exactamente lo que prohíbe §9 —y pintar aquí el aviso de la otra, que
     * es lo que se hacía, es peor: sus botones dicen «esto» señalando los datos de otra—.
     *
     * No se dice nada cuando lo que ocupa está en vuelo: ahí el rótulo del botón —«Guardando…»,
     * «Enviando…»— ya lo está diciendo junto al control, y dos textos para lo mismo acaban
     * contando cosas distintas.
     */
    motivoCerradoFuera: function(ambito) {
      const sinResolver = this.sinResolverDe(ambito);
      if (!sinResolver.length || this.dudaDe(ambito)) return null;
      const ocupa = this.quienOcupa(ambito);
      if (ocupa && ocupa.estado === EN_VUELO) return null;
      return 'Esto sigue cerrado por ' + queEsDe(sinResolver[0].entrada) + ', cuyo resultado no se'
        + ' sabe. No es la que tiene sus datos delante, así que su aviso está arriba, en'
        + ' «Operaciones sin resolver», con «Volver a intentarlo» y «Comprobar cómo quedó»:'
        + ' resuélvela allí y esto se suelta.';
    },
    /** Qué operación tiene en curso esa regla y de qué tipo, o `null`. Es un dato, no el cerrojo. */
    enCursoDe: function(idRegla) {
      if (!idRegla) return null;
      const suyas = this.apuntes().filter(function(apunte) {
        return apunte.estado === EN_VUELO && apunte.entrada.entidad === idRegla;
      });
      if (!suyas.length) return null;
      // Si hubiera un envío y un cambio a la vez, manda el envío: es lo que se rotula aparte.
      return suyas.some(function(apunte) { return apunte.entrada.accion === 'ENVIO_REGLA'; })
        ? 'ENVIO' : 'CAMBIO';
    },
    /**
     * Saca de la cola de un ámbito lo que ya está cerrado y solo seguía ahí por su borrador.
     *
     * Lo llama **el único sitio** en el que ese borrador deja de ser lo que hay delante: lanzar una
     * operación NUEVA en ese ámbito, que se manda con lo que hay escrito. Sin esto, al terminar la
     * nueva volvería a aparecer delante el borrador de la vieja.
     *
     * No lo llaman ni el reintento —repite su propio borrador, no el que esté delante— ni
     * «Limpiar», que tira **lo que hay delante y solo eso**: barrer la cola entera se llevaba
     * borradores que quien pulsaba no había visto.
     */
    olvidarRepetiblesDe: function(ambito, salvo) {
      const self = this;
      // Las que sean, en un solo cambio: con una transición por apunte, cada retirada proyectaría y
      // el ámbito iría enseñando el borrador de la siguiente hasta quedarse vacío.
      self.cambiarRegistro(function() {
        self.colaDe(ambito).forEach(function(apunte) {
          if (apunte.estado === REPETIBLE && apunte.clave !== salvo) {
            self.olvidarOperacion(apunte.clave);
          }
        });
      });
    },

    // ======================================================================== enviar reporte

    abrirEnvio: function() {
      this.envio.error = null;
      this.envio.abierto = true;
    },
    enviarAhora: function() {
      const destinatarios = correosDe(this.envio.emails);
      if (!destinatarios.length) return;
      // La comprobación va también en el método y no solo en el `:disabled` del botón: entre la
      // pulsación y el repintado hay un hueco, y el teclado llega al botón sin pasar por el ratón.
      // Cada POST manda un correo de verdad, así que dos son dos informes al mismo buzón.
      if (this.envioBloqueado) return;
      this.operar(AMBITO_ENVIO, 'ENVIO', null, {
        emails: destinatarios,
        asunto: this.envio.asunto && this.envio.asunto.trim() ? this.envio.asunto.trim() : null
      });
    },

    // ======================================================================== programar reporte

    abrirProgramaciones: function() {
      this.programacion.abierto = true;
      // Si quedó un guardado en vuelo o una respuesta en duda, el formulario NO se limpia: lo que
      // hay escrito y la clave de esa operación son los de esa escritura, y tirarlos deja cerrada
      // la única salida que había. Lo que se podía rellenar y guardar después era un alta nueva, a
      // ciegas, sobre algo que quizá ya estaba dado de alta.
      //
      // Cerrar el popup no cancela nada: la petición sigue en vuelo y el servidor contesta igual.
      // Por eso al volver a abrirlo se enseña dónde se quedó, con sus botones.
      //
      // Y tampoco se limpia lo que haya escrito sin guardar, aunque no haya nada en vuelo: cerrar
      // el popup —con la X, o sin querer— no es pedir que se tire un alta a medias. Se tira con
      // «Limpiar», que está ahí mismo.
      //
      // BARRIDO (16/10/2026): se pregunta por la COLA y no por el cerrojo. El cerrojo no cuenta las
      // operaciones que el servidor ya ha dicho que no se aplicaron —un `NO_CONSTA`, un rechazo—,
      // porque esas no bloquean nada; pero su borrador es el que está delante, y es lo único con lo
      // que decidir qué hacer con ellas. Con el cerrojo aquí, cerrar y reabrir el popup las tiraba en
      // silencio, que es el mismo defecto de la ronda 5 en el otro estado.
      if (!this.quienOcupa(AMBITO_FORM) && !this.formTocado) this.limpiarForm();
      this.cargarProgramaciones();
    },
    /**
     * Las reglas y el historial, que se recargan juntos: un envío cambia las dos cosas —la marca
     * de «último envío» de la regla y el apunte del historial—, así que leer solo una dejaría la
     * otra diciendo lo de antes.
     *
     * Es **informativo y nada más**: ninguna duda se resuelve con esto. Lo que la resuelve es la
     * consulta de la propia operación (`comprobar`), porque estas dos listas no pueden contestar
     * por un envío directo —que no se registra— ni por una operación de una pestaña cerrada.
     */
    cargarProgramaciones: function() {
      this.cargarReglas();
      this.cargarEnvios();
    },
    /** El historial de envíos de este informe. Es una lectura: no escribe nada. */
    cargarEnvios: function() {
      const self = this;
      self.programacion.envios.error = null;
      self.carrilEnvios.lanzar(FactCobro.enviosInformeAbonos(ENVIOS_QUE_SE_ENSENAN), {
        exito: function(respuesta) {
          self.programacion.envios.lista = (respuesta && respuesta.list) || [];
        },
        limpiar: function() {
          self.programacion.envios.lista = [];
        },
        fallo: function(e) {
          self.programacion.envios.error = mensajeDeFallo(e,
            'No se ha podido cargar el historial de envíos.');
        }
      });
    },
    cargarReglas: function() {
      const self = this;
      self.programacion.error = null;
      self.carrilProgramaciones.lanzar(FactCobro.programacionesAbonos(), {
        exito: function(respuesta) {
          self.programacion.lista = (respuesta && respuesta.list) || [];
        },
        limpiar: function() {
          self.programacion.lista = [];
        },
        fallo: function(e) {
          self.programacion.error = mensajeDeFallo(e,
            'No se han podido cargar los envíos programados.');
        }
      });
    },
    /** El botón «Limpiar» / «Cancelar la edición». */
    limpiarForm: function() {
      // Quién puede limpiar el formulario se decide en un solo sitio, y es el mismo cerrojo que el
      // `:disabled` del botón: mientras haya un guardado en vuelo o en duda, lo escrito y la clave
      // de esa operación son de esa escritura y tirarlos impide reintentarla.
      if (this.formBloqueado) return;
      // Esto SÍ es pedir que se tire, pero se tira **lo que hay delante y solo eso**: si detrás
      // queda otra operación que el servidor dijo que no se aplicó, su borrador es lo único con lo
      // que repetirla y quien pulsa aquí no la ha visto nunca. La proyección la pone delante al
      // salir esta, y entonces el formulario no se queda en blanco: se dice, porque un formulario
      // que sigue relleno después de «Limpiar» se lee como que el botón no ha funcionado.
      const delante = this.quienOcupa(AMBITO_FORM);
      if (delante && delante.estado === REPETIBLE) {
        this.olvidarOperacion(delante.clave);
        const siguiente = this.quienOcupa(AMBITO_FORM);
        if (siguiente) {
          this.aviso('Tirado lo de ' + queEsDe(delante.entrada) + '. Delante queda ahora '
            + queEsDe(siguiente.entrada) + ', que tampoco se aplicó: está en «Sin aplicar»,'
            + ' arriba.');
        }
        return;
      }
      this.vaciarForm();
    },
    /**
     * Vacía el formulario. Lo llaman el botón —a través de `limpiarForm`, que es quien mira el
     * cerrojo— y los caminos que ya han resuelto la duda y por tanto pueden vaciarlo.
     */
    vaciarForm: function() {
      this.ponerForm(formEnBlanco(), false);
    },
    /**
     * **Pone el formulario**, y apunta la huella de lo que se ha puesto.
     *
     * Todo cambio del formulario que no venga de quien escribe pasa por aquí —vaciarlo, abrir una
     * regla a editar, devolver un borrador recuperado—, y es lo que hace que `formTocado` signifique
     * «esto lo ha escrito una persona y no está guardado». Una asignación directa a
     * `programacion.form` dejaría la huella vieja.
     */
    ponerForm: function(valores, editando) {
      this.programacion.form = valores;
      this.programacion.huella = huellaForm(valores);
      this.programacion.editando = !!editando;
    },
    /** Vacía el formulario de «Enviar reporte». Los dos campos: el asunto también es lo escrito. */
    vaciarEnvio: function() {
      this.envio.emails = '';
      this.envio.asunto = '';
    },
    editarRegla: function(regla) {
      // El mismo cerrojo que el `:disabled`, por lo mismo que en los demás: entre la pulsación y
      // el repintado hay un hueco, y el teclado llega al botón sin pasar por el ratón.
      if (this.bloqueoDeRegla(regla, 'EDITAR')) return;
      this.ponerForm({
        id: regla.id,
        // El instante de la última modificación que se tenía delante, para que el guardado no
        // escriba encima de lo que alguien haya cambiado mientras el formulario estaba abierto.
        // Va en milisegundos porque tiene que volver IDÉNTICO al milisegundo: el servidor lo usa
        // como filtro de la escritura, y una fecha con formato perdería esa precisión.
        visto: instanteDe(regla.modificadaEn),
        nombre: regla.nombre || '',
        frecuencia: FRECUENCIAS.indexOf(regla.frecuencia) > -1 ? regla.frecuencia : 'SEMANAL',
        hora: regla.hora || '08:00',
        diaSemana: regla.diaSemana || 'LUNES',
        diaMes: regla.diaMes || 1,
        soloLaborables: regla.soloLaborables !== false,
        emailsExtra: (regla.emailsExtra || []).join(', '),
        activa: !!regla.activa
      }, true);
    },
    guardarRegla: function() {
      if (!this.programacionGuardable) return;
      // Mismo motivo que en `enviarAhora`, y aquí el daño es otro: el formulario no se vacía hasta
      // que vuelve la respuesta, así que la segunda pulsación manda el mismo alta otra vez con
      // `id: null` y quedan dos programaciones activas iguales mandando el informe dos veces.
      if (this.formBloqueado) return;
      const form = this.programacion.form;
      // Y una EDICIÓN es además una escritura sobre una regla de la lista, así que no se manda si
      // esa regla ya tiene algo en vuelo: serían dos escrituras a la vez y, si la edición termina
      // después, guarda el `activa` que traía el formulario y deshace lo otro.
      //
      // Aquí se pregunta por `enVuelo` y no por el cerrojo entero: el cerrojo incluye «esta regla
      // se está editando abajo», que es precisamente el caso de este botón. El cerrojo guarda la
      // ficha de la regla; este botón es el formulario, que es quien manda.
      if (this.enVuelo(form)) return;
      // El módulo lo pone el servidor, así que no se manda: mandarlo no sirve de nada y escribirlo
      // aquí invitaría a cambiarlo.
      this.operar(AMBITO_FORM, form.id ? 'EDICION' : 'ALTA', form.id, {
        id: form.id,
        visto: form.visto === undefined ? null : form.visto,
        nombre: form.nombre.trim(),
        frecuencia: form.frecuencia,
        hora: form.hora,
        diaSemana: form.frecuencia === 'SEMANAL' ? form.diaSemana : null,
        diaMes: form.frecuencia === 'MENSUAL' ? form.diaMes : null,
        soloLaborables: !!form.soloLaborables,
        emailsExtra: correosDe(form.emailsExtra),
        activa: !!form.activa
      });
    },
    alternarRegla: function(regla) {
      if (this.bloqueoDeRegla(regla)) return;
      // Se manda el estado que la pantalla tenía delante y el servidor escribe el contrario SOLO si
      // la regla sigue así. Antes era un «lee, niega y guarda» en el servidor: dos pulsaciones casi
      // a la vez leían las dos lo mismo y la segunda deshacía la primera sin decirlo.
      this.operar(AMBITO_REGLA + regla.id, 'ALTERNAR', regla.id, {
        activaActual: !!regla.activa,
        nombre: nombreDe(regla)
      });
    },
    borrarRegla: function(regla) {
      const self = this;
      if (self.bloqueoDeRegla(regla)) return;
      self.$f7.dialog.confirm(
        'Se deja de mandar el informe con esta programación. Los que ya se mandaron no se tocan.',
        '¿Eliminar «' + nombreDe(regla) + '»?',
        function() {
          // El cerrojo se pone al confirmar y no al pulsar «Eliminar»: entre las dos cosas está el
          // diálogo, y dejarlo puesto mientras alguien lo lee bloquearía la regla por nada. Lo que
          // se evita aquí es el segundo borrado, que contestaría «esa programación no existe» y se
          // leería como un fallo cuando lo que pasó es que ya estaba borrada. Y de paso la regla
          // que alguien haya abierto a editar mientras leía el diálogo.
          if (self.bloqueoDeRegla(regla)) return;
          // El borrado también lleva lo que se tenía delante: entre ver la lista y confirmar cabe
          // que alguien le haya cambiado la hora o los destinatarios, y entonces lo que se
          // borraría no es lo que se estaba mirando.
          self.operar(AMBITO_REGLA + regla.id, 'BORRADO', regla.id, {
            visto: instanteDe(regla.modificadaEn),
            nombre: nombreDe(regla)
          });
        });
    },
    /** Manda ahora el informe de esa regla. */
    enviarRegla: function(regla) {
      // Cada pulsación inicia un envío de verdad, así que dos son el mismo informe dos veces en el
      // buzón de los destinatarios de la regla.
      if (this.bloqueoDeRegla(regla)) return;
      this.operar(AMBITO_REGLA + regla.id, 'ENVIO_REGLA', regla.id, { nombre: nombreDe(regla) });
    },
    /**
     * El estado desconocido de esa regla, o `null`: el de la operación que ocupa su ficha, por lo
     * mismo que en los dos ámbitos con campos (`dudaDe`). Si la que ocupa está resuelta y la que
     * sigue en duda es otra, el aviso de esa está arriba y el cerrojo lo dice.
     */
    pendienteDe: function(regla) {
      return (regla && regla.id && this.dudaDe(AMBITO_REGLA + regla.id)) || null;
    },

    // ======================================================================== fontanería

    /**
     * Si esa regla tiene una operación en vuelo. Es un **dato**, no el cerrojo: quien decide si se
     * puede escribir en una regla es `bloqueoDeRegla`, y es el único que lo decide.
     */
    enVuelo: function(regla) {
      return !!(regla && this.enCursoDe(regla.id));
    },
    /** Si esa regla es la que el formulario de abajo tiene abierta ahora mismo. */
    enEdicion: function(regla) {
      const form = this.programacion.form;
      return !!(this.programacion.editando && form.id && regla && regla.id
        && form.id === regla.id);
    },
    /**
     * **El cerrojo de una regla, en un solo sitio**: por qué no se puede escribir en ella ahora
     * mismo, con la frase que se le enseña a quien lo intenta (§9), o `null` si sí se puede.
     *
     * Lo usan los cuatro botones de la ficha —en su `:disabled` y en la primera línea de su
     * método— y el aviso de debajo. Tenerlo repartido es lo que ha costado tres vueltas de
     * revisión: cada condición nueva se añadía al botón que la había destapado y los de al lado
     * seguían pulsando. Si mañana hay un botón más, pregunta aquí y ya está cubierto.
     *
     * Las tres cosas que lo cierran, y por qué:
     *
     * - **una operación en vuelo de esa regla** — cada pulsación es una petición que no se puede
     *   deshacer (un envío de verdad, un cambio de estado, un borrado), y dos seguidas antes de
     *   que vuelva la primera son dos. Es por regla: tener una en marcha no bloquea las demás.
     * - **que esa regla se esté editando abajo** — el formulario guarda la regla ENTERA, incluido
     *   `activa`. Pausarla aquí y guardar después vuelve a escribir el `activa` que el formulario
     *   trae de cuando se abrió, y la pausa se deshace sin que nadie lo diga.
     * - **un guardado del formulario en vuelo, solo para «Editar»** — «Editar» no escribe en el
     *   servidor, pero SUSTITUYE el formulario, y el formulario se limpia al volver la respuesta
     *   del guardado: abrir otra regla mientras tanto la dejaría borrada de debajo sin que nadie
     *   la hubiera cancelado.
     * - **cambios sin guardar en el formulario, solo para «Editar»** — por lo mismo y sin que haya
     *   nada en vuelo: «Editar» sustituye el formulario entero, así que con un alta a medias o una
     *   edición ya cambiada debajo, abrir otra regla tiraba todo lo escrito sin decir nada. Quien
     *   lo pulsa no está pidiendo descartar nada, y lo tirado no se puede recuperar.
     * - **una operación de esa regla cuyo resultado no se sabe** — la respuesta no volvió, así que
     *   puede estar aplicada. Hacer otra cosa encima escribiría sobre un estado que nadie conoce:
     *   primero se resuelve la duda reintentando la misma operación o preguntándole al servidor
     *   cómo quedó.
     *
     * @param accion `'EDITAR'` para el botón de editar; nada para los que escriben en el servidor.
     */
    bloqueoDeRegla: function(regla, accion) {
      if (this.enviando(regla)) {
        return 'Se está mandando el informe de esta programación; los botones vuelven en cuanto el'
          + ' servidor conteste.';
      }
      if (this.enVuelo(regla)) {
        return 'Se está guardando el cambio de esta programación; los botones vuelven en cuanto el'
          + ' servidor conteste.';
      }
      // El cerrojo sale de la COLA de la regla y no del aviso que se esté pintando. El aviso es el
      // de la operación que ocupa la ficha, así que con dos de la misma regla —la primera resuelta
      // como no aplicada y la segunda todavía en duda— preguntar por el aviso dejaba los cuatro
      // botones abiertos con una operación sin resolver encima. Y se dice DÓNDE está su aviso, que
      // no siempre es aquí abajo (§9).
      if (this.enDudaDe(AMBITO_REGLA + regla.id).length) {
        return 'Hay una operación de esta programación cuyo resultado no se sabe. Resuélvela con'
          + ' «Volver a intentarlo» o «Comprobar cómo quedó», '
          + (this.pendienteDe(regla) ? 'aquí abajo' : 'arriba en «Operaciones sin resolver»')
          + ', antes de hacerle otra cosa.';
      }
      if (this.enEdicion(regla)) {
        return 'Esta programación se está editando en el formulario de abajo: guárdala o pulsa'
          + ' «Cancelar la edición» allí antes de pausarla, mandarla o eliminarla. Lo que se guarde'
          + ' abajo manda sobre la regla entera, así que un cambio de aquí se perdería.';
      }
      if (accion === 'EDITAR' && this.formBloqueado) {
        return 'Se está guardando el formulario de abajo; «Editar» vuelve en cuanto el servidor'
          + ' conteste, para no borrar lo que hay escrito.';
      }
      if (accion === 'EDITAR' && this.quienOcupa(AMBITO_FORM)) {
        // BARRIDO (16/10/2026): el cerrojo de arriba no cuenta lo que el servidor ya ha dicho que no
        // se aplicó, porque eso no bloquea escribir. Pero «Editar» no escribe: SUSTITUYE el
        // formulario, y lo que hay dentro es el borrador de esa operación —lo único con lo que
        // repetirla o saber qué era—. Sustituirlo lo tira sin que nadie lo haya pedido.
        return 'El formulario tiene delante una operación que no se aplicó, y abrir esta'
          + ' programación se la llevaría. Repítela o pulsa «Descartar lo escrito» en «Sin'
          + ' aplicar», arriba, y entonces vuelve a pulsar «Editar» aquí.';
      }
      if (accion === 'EDITAR' && this.formTocado) {
        return 'Hay cambios sin guardar en el formulario de abajo y abrir esta programación los'
          + ' descartaría. Guárdalos con «Guardar», o tíralos con «'
          + (this.programacion.editando ? 'Cancelar la edición' : 'Limpiar')
          + '», ahí abajo, y entonces vuelve a pulsar «Editar» aquí.';
      }
      return null;
    },
    /** Si lo que tiene en vuelo es el envío del informe, que es lo que se rotula aparte. */
    enviando: function(regla) {
      return !!regla && this.enCursoDe(regla.id) === 'ENVIO';
    },
    aviso: function(texto) {
      if (!this.$f7 || !this.$f7.toast) return;
      this.$f7.toast.create({ text: texto, position: 'top', closeTimeout: 4000 }).open();
    },
    /** Cómo se rotula la frecuencia de una regla en su ficha. */
    textoFrecuencia: function(regla) {
      const hora = regla.hora || '—';
      if (regla.frecuencia === 'SEMANAL') {
        return 'Cada ' + (regla.diaSemana ? nombreDia(regla.diaSemana) : 'semana') + ' a las ' + hora;
      }
      if (regla.frecuencia === 'MENSUAL') {
        return 'El día ' + (regla.diaMes || '—') + ' de cada mes a las ' + hora;
      }
      if (regla.frecuencia === 'DIARIA') {
        return (regla.soloLaborables ? 'Cada día laborable' : 'Cada día') + ' a las ' + hora;
      }
      return regla.frecuencia || '—';
    },
    /** Cómo acabó un envío del historial, con palabras y no con el código del estado. */
    textoEstadoEnvio: function(envio) {
      if (envio.estado === 'ENVIADO') return 'Enviado';
      if (envio.estado === 'PARCIAL') return 'Enviado a una parte';
      if (envio.estado === 'ERROR') return 'No salió';
      // D-COB-8 · un estado que no se conoce se enseña tal cual; no se da por bueno.
      return envio.estado || '—';
    },
    /**
     * A cuántos buzones salió, de los que se pedían. Se enseñan los dos números porque un
     * «3» suelto no distingue «salió a los tres» de «se pedían diez».
     *
     * Un dato que falta es un guion y NO un cero (D-COB-8): `Number(null)` da cero, y un «0 de 3»
     * inventado diría que el envío no llegó a nadie cuando lo que pasa es que no se sabe.
     */
    textoBuzonesEnvio: function(envio) {
      const ok = numeroOnada(envio.enviadosOk);
      const total = numeroOnada(envio.totalDestinatarios);
      if (ok === null || total === null) return '—';
      return ok + ' de ' + total;
    },
    textoDestinatarios: function(regla) {
      const emails = regla.emailsExtra || [];
      if (!emails.length) {
        // Se dice, porque es el caso en el que la regla no manda nada y no lo parece.
        return 'Sin destinatarios: no se manda a nadie.';
      }
      return 'A: ' + emails.join(', ');
    },
    /** Euros con céntimos y su unidad, o un guion SIN unidad cuando no hay dato (D-COB-8). */
    formatEuros: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Number(valor).toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + ' €';
    },
    palabraAbonos: function(cuantos) {
      return Number(cuantos) === 1 ? 'abono' : 'abonos';
    },
    /** Una fecha con hora del servidor, para el «último envío» de una regla. */
    formatFechaHora: function(valor) {
      if (!valor) return '—';
      const fecha = new Date(valor);
      return isNaN(fecha.getTime()) ? String(valor) : fecha.toLocaleString('es-ES');
    }
  }
};

/** Un número de verdad, o `null` si no hay dato. Un hueco NO es un cero (D-COB-8). */
function numeroOnada(valor) {
  if (valor === null || valor === undefined || valor === '') return null;
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : null;
}

/** El día del mes que acepta el planificador: 1-28, para que exista en febrero. */
function diaDelMesValido(dia) {
  const numero = Number(dia);
  return Number.isInteger(numero) && numero >= 1 && numero <= 28;
}


/**
 * La clave de una **operación de escritura** de este informe.
 *
 * Es lo que permite reintentar sin duplicar: el servidor la acepta una sola vez y, si vuelve con el
 * mismo contenido, contesta el resultado que ya dio. Decisión técnica de @Donchurru del 15/10/2026,
 * tras cuatro rondas de revisión sobre «dos escrituras a la vez sobre la misma programación».
 *
 * Lo que hace falta no es que sea criptográfica: es que **no se repita** entre dos operaciones
 * distintas, porque una clave repetida con otro contenido el servidor la rechaza y con el mismo
 * contenido devolvería el resultado de la otra.
 *
 * Aun así no se usa el generador pseudoaleatorio del lenguaje —lo marcaba Sonar como punto de
 * seguridad (javascript:S2245) y aquí no hace falta pasar por ahí—:
 * `crypto.randomUUID` cuando está, que es el caso de todos los navegadores que soporta la ERP sobre
 * HTTPS; `crypto.getRandomValues` cuando no, que llega hasta los más viejos y también fuera de
 * HTTPS; y como último recurso, sin azar ninguno, el reloj más un contador de esta pestaña, que no
 * se repite porque el contador no vuelve atrás.
 */
let operacionesPedidas = 0;

function nuevaOperacion() {
  const cripto = typeof crypto !== 'undefined' ? crypto : null;
  if (cripto && typeof cripto.randomUUID === 'function') {
    return cripto.randomUUID();
  }
  if (cripto && typeof cripto.getRandomValues === 'function') {
    const octetos = cripto.getRandomValues(new Uint8Array(16));
    let hex = '';
    for (let i = 0; i < octetos.length; i++) {
      hex += ('0' + octetos[i].toString(16)).slice(-2);
    }
    return 'op-' + hex;
  }
  operacionesPedidas += 1;
  // El `performance.now()` entra para que dos pestañas abiertas en el mismo milisegundo no saquen
  // la misma: su origen de tiempos es el de cada pestaña, no el del reloj.
  const dePestana = typeof performance !== 'undefined' && performance.now
    ? Math.trunc(performance.now() * 1000).toString(36)
    : '0';
  return 'op-' + Date.now().toString(36) + '-' + dePestana + '-' + operacionesPedidas;
}

/**
 * Si el resultado de una escritura **no se sabe**, y por eso no se puede afirmar que no se hizo.
 *
 * La regla es la lectura de siempre de los códigos HTTP, y la API la respeta a propósito:
 *
 * - **`4xx`, menos los timeouts** — el servidor ha RECHAZADO la petición y no ha escrito nada. Lo
 *   dice él, y por eso se puede afirmar: `400` no pasó la validación o no hay destinatarios, `403`
 *   sin permiso, `404` la regla no está, `409` la regla ya no es la que se tenía delante o la clave
 *   de operación se ha reutilizado con otro contenido. En todos, el filtro de la escritura no
 *   encontró nada que escribir.
 * - **todo lo demás** — queda en duda. Un `status: 0` o un timeout de pasarela es una respuesta que
 *   no llegó con el cambio **quizá aplicado**; un `5xx` puede caerse después de haber escrito; y el
 *   `503` con el que la API contesta «la misma operación sigue en curso» es literalmente eso.
 *
 * LOS TIMEOUTS CON CÓDIGO `4xx` SON LA EXCEPCIÓN, y hay que nombrarla porque es un `4xx` que NO
 * quiere decir «te he rechazado». `DatabaseModel.sendRequest` clasifica expresamente el **408**
 * como un timeout en el que el servidor puede haber aplicado el cambio —lo trata igual que un 504 o
 * un 524—, así que leerlo como un rechazo descartaba la clave y presentaba como rechazada una
 * escritura que podía estar hecha; al corregir el formulario y volver a guardar, salían dos. Por
 * eso la lista de timeouts es **la misma** que la de ese modelo, y está ahí arriba: dos listas que
 * se consultan por separado acaban diciendo cosas distintas.
 *
 * El caso por omisión es la duda y no la certeza, a propósito: un código inesperado cae del lado
 * prudente. Al revés, equivocarse afirma «no se ha guardado» sobre algo guardado, que es el fallo
 * que esto viene a cerrar. Y como el reintento lleva la misma clave de operación, dudar no cuesta
 * nada: lo peor que pasa es repetir una petición que el servidor no aplica dos veces.
 */
function resultadoEnDuda(e) {
  const estado = e ? Number(e.status) : NaN;
  if (TIMEOUTS_CON_CAMBIO_QUIZA_APLICADO.indexOf(estado) > -1) return true;
  return !(estado >= 400 && estado < 500);
}

/**
 * Qué se le cuenta a quien está delante cuando el resultado no se sabe.
 *
 * Tres cosas, y las tres hacen falta: **qué no se sabe**, **que reintentar es seguro** —que es lo
 * que desbloquea la situación, y no es evidente— y **el motivo que haya dado el servidor**, si lo
 * ha dado. Lo que NO se dice es «no se ha guardado»: puede estar guardado, y decirlo llevaba a dar
 * de alta lo mismo otra vez.
 *
 * El `que` es un complemento directo en minúscula, igual que en `avisoNoGuardado`: se escribe
 * detrás de «No se sabe si ha llegado a aplicarse ».
 */
function textoEnDuda(e, que) {
  const delServidor = e && typeof e.message === 'string' && e.message.trim() ? e.message.trim() : null;
  return 'No se sabe si ha llegado a aplicarse ' + que + ': la respuesta del servidor no ha '
    + 'llegado o no ha sido concluyente. '
    + (delServidor ? 'El servidor dice: «' + delServidor + '». ' : '')
    + 'Pulsa «Volver a intentarlo» y se repetirá LA MISMA operación, que el servidor reconoce y no '
    + 'aplica dos veces. Si prefieres saber antes qué pasó, «Comprobar cómo quedó» se lo pregunta '
    + 'al servidor por esta operación en concreto.';
}

/**
 * Un apunte del registro, con **los campos que la pantalla va a necesitar ya puestos**.
 *
 * Todo lo que entra en el registro pasa por aquí porque en Vue 2 una clave que no existe al crear el
 * objeto **no es reactiva**: `situacion`, `consultando`, `noSePudo` y `detalle` los escribe
 * `comprobar` después, y si no estuvieran declarados desde el principio el botón no se apagaría
 * mientras pregunta y lo que contestara el servidor no se pintaría nunca. `estado` igual: de él
 * dependen el cerrojo y los avisos.
 *
 * Y lleva dentro la **entrada entera** —ámbito, usuario, acción, entidad, clave y borrador—, que es
 * lo que hace que «Volver a intentarlo» repita la MISMA operación sin reconstruir nada y que siga
 * funcionando después de recargar la página, cuando no queda ninguna función en memoria.
 *
 * @param entrada la identidad de la operación, tal como se apuntó antes de lanzarla.
 * @param estado  `EN_VUELO`, `EN_DUDA` o `REPETIBLE`.
 * @param texto   lo que se le cuenta a quien está delante mientras no se ha preguntado.
 */
function apunteDe(entrada, estado, texto) {
  return {
    /** En qué estado está: es lo que cierra el control y lo que decide si se pinta su aviso. */
    estado: estado,
    texto: texto,
    entrada: entrada,
    /** La clave de la operación; es por lo que se pregunta y lo que se repite. */
    clave: entrada.clave,
    /**
     * Cuándo empezó la operación. Es lo que ordena la cola de cada ámbito y por tanto lo que decide
     * quién ocupa el control: el más antiguo, que es el que tiene delante quien mira.
     *
     * Viene del apunte del navegador cuando la operación se recupera después de recargar, para que
     * el orden de la cola sea el de siempre y no el de la recuperación. Un reintento **no** lo
     * cambia: `operacionesAbonosCobros.guardar` conserva el original a propósito.
     */
    cuando: entrada.cuando || Date.now(),
    /**
     * Lo que el servidor ha contestado de ella: `NO_CONSTA`, `EN_CURSO`, `DESCONOCIDO` o `HECHA`.
     * `POR_CONSULTAR` mientras no se le haya preguntado, que es un estado de la pantalla y no del
     * servidor: con él se ofrecen las dos salidas y no se afirma nada.
     */
    situacion: SITUACION_POR_CONSULTAR,
    /** Si se está preguntando ahora mismo, para no lanzar dos consultas. */
    consultando: false,
    /** Lo que contestó el servidor, cuando hay algo concreto que enseñar. */
    detalle: null,
    /** Por qué no se ha podido preguntar; `null` si no ha fallado. */
    noSePudo: null,
    /**
     * Si la MISMA operación se puede volver a lanzar con su misma clave.
     *
     * Declarado desde el principio por lo mismo que los de arriba: lo escribe `apuntarSinAplicar`
     * después, y en Vue 2 una clave añadida a mano no sería reactiva —el botón no se apagaría—.
     * Arranca en `true` porque mientras no se sepa lo contrario repetir es la salida segura: el
     * servidor reconoce la clave y no aplica la operación dos veces.
     */
    sePuedeRepetir: true,
    /**
     * Si su clave **no está guardada en el navegador** aunque la operación siga sin resolverse.
     *
     * Lo mantiene `conservarApuntes`, que repone el apunte en cada transición; esto es `true` solo
     * cuando ni reponiéndolo se ha podido —el almacén está lleno o bloqueado—. Entonces la clave vive
     * únicamente en esta pestaña, así que hay que decirlo junto al aviso: lo que se pierde si se
     * recarga es la posibilidad de preguntar cómo acabó.
     *
     * Declarado aquí por lo mismo que los de arriba: en Vue 2 una clave añadida después no sería
     * reactiva y el aviso no se pintaría nunca.
     */
    sinApuntar: false
  };
}

/**
 * El instante de una fecha del servidor en milisegundos, o `null` si no hay.
 *
 * Sirve para el `visto` que viaja de vuelta como filtro de la escritura, así que lo que importa es
 * que **no pierda precisión**: `new Date()` entiende tanto el número de milisegundos como el texto
 * ISO, que son las dos formas en las que Jackson puede escribir una fecha, y las dos conservan los
 * milisegundos. Un `null` aquí quiere decir «la regla no tenía fecha de modificación», y el
 * servidor lo comprueba como tal.
 */
function instanteDe(valor) {
  if (valor === null || valor === undefined || valor === '') return null;
  const fecha = new Date(valor);
  const instante = fecha.getTime();
  return Number.isFinite(instante) ? instante : null;
}

/**
 * **Lanza una operación, y es el único sitio donde se decide qué petición es cada acción.**
 *
 * Está fuera del componente a propósito: lo que recibe es una `entrada`, que es exactamente lo que
 * `operacionesAbonosCobros` guarda en el navegador. Así una operación **recuperada después de
 * recargar** se lanza por el mismo camino y con la misma clave que la original, sin necesitar
 * ninguna función guardada en memoria —que es lo que antes se perdía con la pestaña—.
 *
 * Y es lo que hace que las seis operaciones de esta pantalla tengan las mismas garantías: cuando
 * cada una tenía su propio `then`/`catch`, cada arreglo se quedaba en la que lo había destapado.
 */
function lanzarOperacion(entrada) {
  const borrador = entrada.borrador || {};
  if (entrada.accion === 'ENVIO') {
    return FactCobro.enviarInformeAbonos({
      emails: borrador.emails, asunto: borrador.asunto
    }, entrada.clave);
  }
  if (entrada.accion === 'ALTA' || entrada.accion === 'EDICION') {
    return FactCobro.guardarProgramacionAbonos(borrador, entrada.clave);
  }
  if (entrada.accion === 'ALTERNAR') {
    return FactCobro.alternarProgramacionAbonos(entrada.entidad, !!borrador.activaActual,
      entrada.clave);
  }
  if (entrada.accion === 'BORRADO') {
    return FactCobro.borrarProgramacionAbonos(entrada.entidad, borrador.visto, entrada.clave);
  }
  if (entrada.accion === 'ENVIO_REGLA') {
    return FactCobro.enviarProgramacionAbonos(entrada.entidad, entrada.clave);
  }
  // Un apunte de una versión anterior con una acción que ya no existe. Se rechaza en vez de no
  // hacer nada: una promesa que nunca se resuelve dejaría el aviso diciendo «comprobando…» para
  // siempre.
  return Promise.reject({ message: 'Esta operación ya no se reconoce; recarga la pantalla.' });
}

/**
 * Cómo se nombra una operación en un aviso: el complemento que va detrás de «No se sabe si ha
 * llegado a aplicarse ».
 *
 * Sale de la `entrada` y no de una cadena que pase cada llamante, por lo mismo que `lanzarOperacion`:
 * después de recargar no queda nada en memoria, y un aviso recuperado tiene que poder decir de qué
 * está hablando. El nombre de la regla va en el borrador justamente para esto.
 */
function queEsDe(entrada) {
  const nombre = (entrada.borrador && entrada.borrador.nombre) || null;
  const comillas = nombre ? ' «' + nombre + '»' : '';
  if (entrada.accion === 'ENVIO') return 'el envío del informe por correo';
  if (entrada.accion === 'ALTA') return 'el alta del envío programado' + comillas;
  if (entrada.accion === 'EDICION') return 'el cambio del envío programado' + comillas;
  if (entrada.accion === 'ALTERNAR') return 'el cambio de' + (comillas || 'l envío programado');
  if (entrada.accion === 'BORRADO') return 'el borrado de' + (comillas || 'l envío programado');
  if (entrada.accion === 'ENVIO_REGLA') return 'el envío de' + (comillas || 'l informe programado');
  return 'esa operación';
}

/**
 * Lo que el servidor contestó de una operación que **sí terminó**, escrito para quien está delante.
 *
 * Solo lo que se puede decir con lo que viaja en la respuesta: el recuento de un envío, el estado
 * con el que acabó. La consulta no devuelve la regla con sus destinatarios —no expone contenido
 * privado— así que de un alta lo único que se dice es que quedó guardada.
 */
function detalleDe(entrada, estado) {
  if (estado.situacion !== SITUACION_HECHA) return null;
  if (entrada.accion === 'ENVIO') {
    const cuantos = numeroOnada(estado.resultado);
    return cuantos === null
      ? 'el correo salió'
      : 'el correo salió a ' + cuantos + (cuantos === 1 ? ' destinatario' : ' destinatarios');
  }
  if (entrada.accion === 'ENVIO_REGLA') {
    return estado.resultado ? 'el envío acabó con estado ' + estado.resultado : 'el envío terminó';
  }
  if (entrada.accion === 'BORRADO') return 'la programación quedó borrada';
  return 'la programación quedó guardada';
}

/** Con la primera letra en mayúscula, para cuando la frase empieza un renglón. */
function conMayuscula(texto) {
  const frase = String(texto || '');
  return frase ? frase.charAt(0).toUpperCase() + frase.slice(1) : frase;
}

/**
 * Quién está delante, que es parte de la identidad de una operación.
 *
 * Hace falta porque los apuntes viven en `localStorage`, que es del navegador y no de la sesión: sin
 * esto, los de quien usó antes este equipo se le enseñarían al siguiente, con su borrador y sus
 * destinatarios dentro.
 */
function usuarioDeAhora() {
  const usuario = Utils.getUser();
  return (usuario && usuario.username) || null;
}

/** Cómo se nombra una regla en un aviso. */
function nombreDe(regla) {
  return (regla && regla.nombre) || 'el envío';
}

/** El mensaje del servidor si lo trae, y si no el genérico. */
function mensajeDeFallo(e, generico) {
  if (e && e.status === 403) {
    return 'El informe de abonos abiertos es de Administración, Directores y Administrador.';
  }
  return (e && e.message) ? e.message : generico;
}
</script>
